import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Button,
  Label,
  Nav,
} from "reactstrap";
import { Table, Input, Select } from "antd";
import { Icon } from "../../components/Component";
import { useNavigate, useLocation } from "react-router-dom";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import { useDispatch, useSelector } from "react-redux";
import { updateMenuData } from "../../store/slices/misc/menuData";
import ReportInDetailModal from "./ReportInDetailModal";
import TopMenu from "../../route/topMenu";
import FormInput from "../form-components/InputComponent";
import FormTextArea from "../form-components/TextAreaComponent";
import FormDatePicker from "../form-components/DatePicker";
import FormSelect from "../form-components/SelectComponent";
import { FormProvider, useForm } from "react-hook-form";
import { expensesApis } from "../../api/Expenses";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import { getDirtyFieldsOnly } from "../../utils/use-form/GetDirtyFieldsOnly";
import { extractReceiptData, ReceiptOCRData } from "../../utils/ocr/ReceiptOCR";

const NewExpense: React.FC = () => {
  const dispatch = useDispatch();
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const [loading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [displayedImageIndex, setDisplayedImageIndex] = useState(0);
  const [imageZoom, setImageZoom] = useState(1);
  const [isImageZoomed, setIsImageZoomed] = useState(false);
  const [displayedPdfIndex, setDisplayedPdfIndex] = useState(0);
  const [pdfPageNumbers, setPdfPageNumbers] = useState<Record<number, number>>({});
  const [currentPdfPage, setCurrentPdfPage] = useState<Record<number, number>>({});
  const [pdfPageImages, setPdfPageImages] = useState<Record<string, string>>({});
  const [loadingPdfPages, setLoadingPdfPages] = useState<Record<number, boolean>>({});
  const navigate = useNavigate();
  const location = useLocation();

  const [isItemized, setIsItemized] = useState(false);
  const [selectedTaxType, setSelectedTaxType] = useState('GST Not Applicable');
  const [goodsOrService, setGoodsOrService] = useState('Service');
  const [itemizedExpenses, setItemizedExpenses] = useState<Array<{
    id: number;
    category: string;
    itemName: string;
    description: string;
    amount: number;
    type: string;
    tax_percentage: string;
    hsnSac: string;
    cgst: number;
    sgst: number;
    igst: number;
    totalTax: number;
    totalAmount: number;
    itemId: number | null;
  }>>([
    { id: 1, category: '', itemName: '', description: '', amount: 0, type: 'Service', tax_percentage: '', hsnSac: '', cgst: 0, sgst: 0, igst: 0, totalTax: 0, totalAmount: 0, itemId: null }
  ]);
  const [amountValidationError, setAmountValidationError] = useState('');
  const [itemizedValidationError, setItemizedValidationError] = useState('');
  const [headerTaxAmount, setHeaderTaxAmount] = useState(0);
  const [headerTotalAmount, setHeaderTotalAmount] = useState(0);
  const [gstinValidationError, setGstinValidationError] = useState('');
  const [projectId, setProjectId] = useState<string | number | null>(null);
  const [reportOptions, setReportOptions] = useState<Array<{ value: string, label: string }>>([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [selectedReportDateRange, setSelectedReportDateRange] = useState<{ startDate: string | null, endDate: string | null }>({ startDate: null, endDate: null });
  const [userOptions, setUserOptions] = useState<Array<{ value: string, label: string }>>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [processingOCR, setProcessingOCR] = useState(false);
  const [ocrData, setOcrData] = useState<ReceiptOCRData | null>(null);
  const [ocrProcessedFileIndex, setOcrProcessedFileIndex] = useState<number | null>(null);
  const [originalFormValues, setOriginalFormValues] = useState<any>(null);
  const [originalItemizedExpenses, setOriginalItemizedExpenses] = useState<any>(null);
  const [originalTaxType, setOriginalTaxType] = useState<string>('GST Not Applicable');
  const [originalGoodsOrService, setOriginalGoodsOrService] = useState<string>('Service');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [customCategoryName, setCustomCategoryName] = useState<string>('');
  const [paymentFromName, setPaymentFromName] = useState<string | null>(null);
  const [expenseTotalAmountFromApi, setExpenseTotalAmountFromApi] = useState<number | null>(null);
  const [expenseSubtotalFromApi, setExpenseSubtotalFromApi] = useState<number | null>(null);
  const [expenseTotalTaxFromApi, setExpenseTotalTaxFromApi] = useState<number | null>(null);
  const reportsFetchInProgressRef = useRef(false);
  const lastExtractedProjectIdRef = useRef<string | number | null>(null);
  const fallbackFetchedForProjectIdRef = useRef<string | number | null>(null);
  const reportOptionsLengthRef = useRef(0);
  reportOptionsLengthRef.current = reportOptions.length;
  const methods = useForm({
    defaultValues: {
      reimbursable: true,
      report: '',
      expenseDate: dayjs(),
      merchant: '',
      gstin2: '',
      paymentMode: '',
      amount: '',
      reference: '',
      description: '',
      taxType: 'GST Not Applicable',
      tax_percentage: '',
      hsnSac: '',
      destinationOfSupply3: '',
      chequeNumber: '',
      chequeDate: undefined,
      currency: 'INR',
      currencyId: 1,
      userId: '',
    }
  });

  const paymentMode = methods.watch("paymentMode");

  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => {
          return {
            ...item,
            linkState: location.state,
          };
        })
      )
    );
  }, [location, dispatch]);

  // Extract projectId from location state - only update when value actually changes to avoid repeated fetches
  useEffect(() => {
    const { id: projectIdFromState, projectId: projectIdFromStateAlt } = location.state || {};
    const finalProjectId = projectIdFromState ?? projectIdFromStateAlt ?? null;
    if (finalProjectId != null && finalProjectId !== lastExtractedProjectIdRef.current) {
      lastExtractedProjectIdRef.current = finalProjectId;
      setProjectId(finalProjectId);
    }
  }, [location.state]);

  // Fetch reports data for dropdown
  const fetchReportsData = async () => {
    if (reportsFetchInProgressRef.current) return;
    const currentProjectId = projectId || location.state?.projectId || location.state?.id;
    if (currentProjectId) {
      reportsFetchInProgressRef.current = true;
      setLoadingReports(true);
      try {
        const response = await expensesApis.fetchReportDropdown(currentProjectId);

        if (response && response.status) {
          const reportsList = response.data || [];

          const transformedOptions = reportsList.map((report: any) => ({
            value: report.expenseReportId?.toString() || report.id?.toString() || '',
            label: report.reportName || report.name || 'Unnamed Report'
          }));

          setReportOptions(transformedOptions);
        } else {
          setReportOptions([]);
        }
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        setReportOptions([]);
      } finally {
        setLoadingReports(false);
        reportsFetchInProgressRef.current = false;
      }
    } else {
      setReportOptions([]);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchReportsData();
    }
  }, [projectId]);

  // Load users for dropdown - only for admin users
  useEffect(() => {
    const loadUsers = async () => {
      if (!projectId || currentUser?.genericRole?.toLowerCase() !== "admin") return;

      setLoadingUsers(true);
      try {
        const response = await expensesApis.fetchProjectUsers(projectId);

        if (response && response.status) {
          const usersList = response.data || [];

          const options = usersList.map((user: any) => ({
            value: user.userId?.toString() || user.id?.toString() || '',
            label: user.userName || user.name || user.email || 'Unnamed User'
          }));

          setUserOptions(options);
        } else {
          setUserOptions([]);
        }
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        setUserOptions([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    loadUsers();
  }, [projectId, currentUser]);

  // Fetch report details when report is selected to get date range
  const fetchReportDetails = async (reportId: string | number) => {
    if (!reportId) {
      setSelectedReportDateRange({ startDate: null, endDate: null });
      return;
    }

    try {
      const response = await expensesApis.fetchReport(reportId);
      if (response && response.status && response.data) {
        const reportData = response.data;
        setSelectedReportDateRange({
          startDate: reportData.startDate || null,
          endDate: reportData.endDate || null
        });
      } else {
        setSelectedReportDateRange({ startDate: null, endDate: null });
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      setSelectedReportDateRange({ startDate: null, endDate: null });
    }
  };

  // Fallback: Retry fetch once per projectId after 1s only if dropdown is still empty (avoids repeated calls)
  useEffect(() => {
    if (!projectId) return;
    const timer = setTimeout(() => {
      if (reportOptionsLengthRef.current === 0 && projectId !== fallbackFetchedForProjectIdRef.current) {
        fallbackFetchedForProjectIdRef.current = projectId;
        fetchReportsData();
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [projectId]);

  // Set report field after reportOptions are loaded - only for edit mode or coming from breakdown
  useEffect(() => {
    const { reportId, expenseData: passedExpenseData, editMode, fromReportBreakDown } = location.state || {};
    if (reportOptions.length > 0 && (editMode || fromReportBreakDown)) {
      if (reportId) {
        methods.setValue('report', reportId.toString());
        fetchReportDetails(reportId);
      } else if (passedExpenseData && editMode && passedExpenseData.expenseReportId) {
        methods.setValue('report', passedExpenseData.expenseReportId.toString());
        fetchReportDetails(passedExpenseData.expenseReportId);
      }
    }
  }, [reportOptions, location.state, methods]);

  const selectedReport = methods.watch('report');
  const [isFormResetting, setIsFormResetting] = useState(false);
  
  useEffect(() => {
    if (isFormResetting || !selectedReport || selectedReport === '' || selectedReport === null || selectedReport === undefined) {
      setSelectedReportDateRange({ startDate: null, endDate: null });
      return;
    }
    
    fetchReportDetails(selectedReport);
  }, [selectedReport, isFormResetting]);

  // Load expense data when in edit mode or auto-populate report from report detail page
  useEffect(() => {
    const loadExpenseForEdit = async () => {
      const { expenseId, expenseData: passedExpenseData, editMode, reportId, reportName, fromReportBreakDown, viewMode } = location.state || {};

      if (fromReportBreakDown && reportId && !expenseId) {
        methods.setValue('report', reportId.toString());
      }

      // Check for paymentFromName in location.state expenseData (for view mode)
      if (location.state?.expenseData?.paymentFromName && currentUser?.genericRole?.toLowerCase() === "admin") {
        setPaymentFromName(location.state.expenseData.paymentFromName);
      }

      if (expenseId && projectId) {
        try {
          let expenseData;

          if (passedExpenseData && editMode) {
            expenseData = passedExpenseData;
          } else {
            const response = await expensesApis.fetchPayment(expenseId);
            if (response && response.status && response.data) {
              expenseData = response.data;
            }
          }

          if (expenseData) {
            // Map API totals to summary - no frontend calculation; use API/PDF values only
            const apiTotalAmount = expenseData.totals?.total_amount ?? expenseData.amount;
            const apiAmount = apiTotalAmount != null ? (typeof apiTotalAmount === 'string' ? parseFloat(apiTotalAmount) : Number(apiTotalAmount)) : null;
            if (apiAmount != null && !Number.isNaN(apiAmount)) setExpenseTotalAmountFromApi(apiAmount);
            const apiSubtotal = expenseData.totals?.subtotal ?? expenseData.totals?.sub_total ?? expenseData.subtotal;
            if (apiSubtotal != null) {
              const n = typeof apiSubtotal === 'string' ? parseFloat(apiSubtotal) : Number(apiSubtotal);
              if (!Number.isNaN(n)) setExpenseSubtotalFromApi(n);
            }
            const apiTotalTax = expenseData.totals?.total_tax ?? expenseData.totalTax ?? expenseData.taxAmount;
            if (apiTotalTax != null) {
              const n = typeof apiTotalTax === 'string' ? parseFloat(apiTotalTax) : Number(apiTotalTax);
              if (!Number.isNaN(n)) setExpenseTotalTaxFromApi(n);
            }
            methods.setValue('report', expenseData.expenseReportId?.toString() || '');
            methods.setValue('expenseDate', expenseData.actionDate ? dayjs(expenseData.actionDate) : dayjs());
            methods.setValue('merchant', expenseData.merchant || '');
            methods.setValue('gstin2', expenseData.gstin || '');
            methods.setValue('paymentMode', expenseData.paymentMode || '');
            methods.setValue('amount', expenseData.amount?.toString() || '');
            methods.setValue('reference', expenseData.referenceId || '');
            methods.setValue('description', expenseData.description || '');
            methods.setValue('reimbursable', expenseData.claimReimbursement !== undefined ? expenseData.claimReimbursement : true);
            
            if (expenseData.paymentMode === 'Cheque') {
              const chequeNumberValue = expenseData.chequeNumber || expenseData.chequeNo || expenseData.checkNumber || '';
              methods.setValue('chequeNumber', chequeNumberValue);

              const rawChequeDate = expenseData.chequeDate || expenseData.checkDate || expenseData.cheque_date || expenseData.check_date;
              if (rawChequeDate) {
                let parsedChequeDate = dayjs();
                if (dayjs(rawChequeDate, 'DD-MMM-YYYY', true).isValid()) {
                  parsedChequeDate = dayjs(rawChequeDate, 'DD-MMM-YYYY');
                } else if (dayjs(rawChequeDate, 'YYYY-MM-DD', true).isValid()) {
                  parsedChequeDate = dayjs(rawChequeDate, 'YYYY-MM-DD');
                } else if (dayjs(rawChequeDate).isValid()) {
                  parsedChequeDate = dayjs(rawChequeDate);
                }
                methods.setValue('chequeDate', parsedChequeDate as any);
              }
            }

            if (expenseData.productCategory) {
              setGoodsOrService(expenseData.productCategory);
            }

            if (expenseData.taxType) {
              setSelectedTaxType(expenseData.taxType);
              methods.setValue('taxType', expenseData.taxType);
            }

            const expenseTaxPercent = expenseData.taxPercent ?? expenseData.tax_percentage;
            if (expenseTaxPercent != null && expenseTaxPercent !== '') {
              methods.setValue('tax_percentage', expenseTaxPercent.toString());
            }

            if (expenseData.hsnCode || expenseData.sacCode) {
              methods.setValue('hsnSac', expenseData.hsnCode || expenseData.sacCode || '');
            }

            if (expenseData.destinationOfSupply || expenseData.destinationOfSupply3 || expenseData.destination) {
              methods.setValue('destinationOfSupply3', expenseData.destinationOfSupply || expenseData.destinationOfSupply3 || expenseData.destination || '');
            }

            // Handle userId field - set directly if available
            if (currentUser?.genericRole?.toLowerCase() === "admin") {
              if (expenseData.userId) {
                methods.setValue('userId', expenseData.userId.toString());
              } else if (expenseData.paymentFromName) {
                // Store paymentFromName to map to userId later when userOptions are loaded
                setPaymentFromName(expenseData.paymentFromName);
              }
            }

            // Calculate tax amounts for edit mode if GST Applicable and not itemized
            if (expenseData.taxType === 'GST Applicable' && !isItemized && expenseData.amount && expenseTaxPercent) {
              const mainAmount = parseFloat(expenseData.amount.toString()) || 0;
              const taxPercent = parseFloat(expenseTaxPercent.toString()) || 0;
              const taxAmount = (mainAmount * taxPercent) / 100;
              setHeaderTaxAmount(taxAmount);
              setHeaderTotalAmount(mainAmount + taxAmount);
            }

            // Handle itemized expenses if they exist

            // Check multiple possible field names for itemized data
            const itemizedData = expenseData.items || expenseData.expenseItems || expenseData.itemizedExpenses || expenseData.lineItems;

            if (itemizedData && Array.isArray(itemizedData) && itemizedData.length > 0) {
              setIsItemized(true);

              // Transform API items to component format
              const transformedItems = itemizedData.map((item: any, index: number) => {
                // Calculate tax amounts based on taxPercent; API may send amount (base) or amountWithoutTax
                const taxPercent = item.taxPercent ?? item.tax_percentage ?? 0;
                const amountWithoutTax = item.amountWithoutTax ?? (typeof item.amount === 'string' ? parseFloat(item.amount) || 0 : Number(item.amount) || 0);
                const totalAmount = amountWithoutTax + (amountWithoutTax * taxPercent / 100);
                const taxAmount = totalAmount - amountWithoutTax;
                
                // For GST, typically split as CGST and SGST (9% each for 18% total)
                // or IGST for inter-state transactions
                let cgst = 0, sgst = 0, igst = 0;
                if (taxPercent > 0) {
                  // Assuming CGST/SGST split for now - this might need adjustment based on business logic
                  cgst = taxAmount / 2;
                  sgst = taxAmount / 2;
                }
                
                return {
                  id: index + 1,
                  category: item.category || '',
                  itemName: item.description || item.itemName || '', // API description maps to Item Name
                  description: item.description || '', // API description for description column
                  amount: amountWithoutTax, // Use amountWithoutTax as the base amount
                  type: goodsOrService, // Use the current goodsOrService state
                  tax_percentage: taxPercent ? `${taxPercent}%` : '',
                  hsnSac: item.hsnCode || item.sacCode || '', // Use hsnCode first, then sacCode
                  cgst: cgst,
                  sgst: sgst,
                  igst: igst,
                  totalTax: taxAmount,
                  totalAmount: totalAmount,
                  itemId: item.itemId // Store the original itemId for edit operations
                };
              });

              setItemizedExpenses(transformedItems);
            } else {
              setIsItemized(false);
              if (expenseData.amount == null) {
                setExpenseTotalAmountFromApi(null);
                setExpenseSubtotalFromApi(null);
                setExpenseTotalTaxFromApi(null);
              }
            }

            // Handle existing receipt images/files from dms array
            if (expenseData.dms && Array.isArray(expenseData.dms) && expenseData.dms.length > 0) {
              try {
                const mockFiles = expenseData.dms.map((dmsItem: any, index: number) => {
                  if (dmsItem && dmsItem.filePath) {
                    const mockFile = new File([''], dmsItem.fileName || `file-${index + 1}`, { 
                      type: dmsItem.fileType || 'image/jpeg' 
                    });
                    // Store the original file data for reference
                    (mockFile as any).originalUrl = dmsItem.filePath;
                    (mockFile as any).docId = dmsItem.docId;
                    (mockFile as any).docHash = dmsItem.docHash;
                    (mockFile as any).blobName = dmsItem.blobName;
                    (mockFile as any).isExisting = true; // Flag to identify existing files
                    
                    
                    return mockFile;
                  }
                  return null;
                }).filter(Boolean);
                setUploadedFiles(mockFiles);
              } catch (error) {
              }
            } else {
            }
          }
        } catch (error: any) {
          const errorMessage = error?.response?.data?.message;
          Swal.fire({
            title: "Error!",
            text: errorMessage,
            icon: "error",
            confirmButtonText: "OK"
          });
        }
      }
    };

    loadExpenseForEdit();
  }, [projectId, location.state, methods]);

  // Handle paymentFromName mapping to userId when userOptions are loaded
  useEffect(() => {
    // Only process if userOptions are loaded, userId is not set, and we have paymentFromName
    if (userOptions.length > 0 && currentUser?.genericRole?.toLowerCase() === "admin" && paymentFromName) {
      const currentUserId = methods.getValues('userId');
      
      // If userId is not set, try to find it from paymentFromName
      if (!currentUserId) {
        const matchedUser = userOptions.find(
          (option: any) => option.label === paymentFromName || 
                          option.label?.toLowerCase() === paymentFromName?.toLowerCase()
        );
        if (matchedUser) {
          methods.setValue('userId', matchedUser.value);
          // Clear paymentFromName after successful mapping
          setPaymentFromName(null);
        }
      } else {
        // Clear paymentFromName if userId is already set
        setPaymentFromName(null);
      }
    }
  }, [userOptions, paymentFromName, currentUser, methods]);

  // Validation functions
  const validateBasicFields = (values: any) => {
    if (!projectId) {
      Swal.fire({
        title: "Error!",
        text: "Project ID is required to create an expense",
        icon: "error",
        confirmButtonText: "OK"
      });
      return false;
    }

    if (!values.report) {
      Swal.fire({
        title: "Error!",
        text: "Please select a report",
        icon: "error",
        confirmButtonText: "OK"
      });
      return false;
    }

    if (!values.merchant || values.merchant.trim() === '') {
      Swal.fire({
        title: "Error!",
        text: "Please provide a valid merchant name",
        icon: "error",
        confirmButtonText: "OK"
      });
      return false;
    }

    if (!values.reference || values.reference.trim() === '') {
      Swal.fire({
        title: "Error!",
        text: "Please provide a reference ID",
        icon: "error",
        confirmButtonText: "OK"
      });
      return false;
    }

    // Validate custom category name when "Others" is selected
    if (selectedCategory === 'Others' && (!customCategoryName || customCategoryName.trim() === '')) {
      Swal.fire({
        title: "Error!",
        text: "Please enter a category name",
        icon: "error",
        confirmButtonText: "OK"
      });
      return false;
    }

    return true;
  };

  const validateGSTINField = (values: any) => {
    if (selectedTaxType !== 'GST Not Applicable') {
      if (!values.gstin2 || values.gstin2.trim() === '') {
        Swal.fire({
          title: "Error!",
          text: "Please provide a valid GSTIN",
          icon: "error",
          confirmButtonText: "OK"
        });
        return false;
      }
      
      if (!validateGSTIN(values.gstin2)) {
        Swal.fire({
          title: "Error!",
          text: gstinValidationError || "Please provide a valid GSTIN format",
          icon: "error",
          confirmButtonText: "OK"
        });
        return false;
      }
    }
    return true;
  };

  const validateAmountFields = (values: any) => {
    const mainAmount = parseFloat(values.amount) || 0;
    const itemizedTotal = calculateItemizedTotal();

    if (isItemized) {
      const hasAnyItems = itemizedExpenses.some(expense =>
        expense.category || expense.description || expense.amount > 0
      );

      if (hasAnyItems) {
        const validItems = itemizedExpenses.filter(expense =>
          expense.category && expense.itemName && expense.description && expense.amount > 0
        );

        if (validItems.length < 2) {
          Swal.fire({
            title: "Validation Error!",
            text: "At least 2 valid items are required when using item split mode. Please fill in category, item name, description, and amount for at least 2 items.",
            icon: "error",
            confirmButtonText: "OK"
          });
          return false;
        }
      }

      if (hasAnyItems && mainAmount === 0 && itemizedTotal === 0) {
        Swal.fire({
          title: "Validation Error!",
          text: "Please provide either a total amount or add itemized expenses",
          icon: "error",
          confirmButtonText: "OK"
        });
        return false;
      }

      if (mainAmount > 0 && itemizedTotal > 0 && amountValidationError) {
        Swal.fire({
          title: "Validation Error!",
          text: amountValidationError,
          icon: "error",
          confirmButtonText: "OK"
        });
        return false;
      }
    } else {
      if (mainAmount === 0) {
        Swal.fire({
          title: "Validation Error!",
          text: "Please provide the expense amount",
          icon: "error",
          confirmButtonText: "OK"
        });
        return false;
      }
    }
    return true;
  };

  const validateDestinationOfSupply = (values: any) => {
    if (goodsOrService === 'Goods' && (!values.destinationOfSupply3 || values.destinationOfSupply3.trim() === '')) {
      Swal.fire({
        title: "Error!",
        text: "Destination of Supply is required for Goods category",
        icon: "error",
        confirmButtonText: "OK"
      });
      return false;
    }
    return true;
  };

  const validateReportAccess = async (reportId: string) => {
    try {
      const reportValidation = await expensesApis.fetchReport(reportId);
      if (!reportValidation || !reportValidation.status) {
        Swal.fire({
          title: "Error!",
          text: "Selected expense report is not accessible or does not exist. Please select a valid report.",
          icon: "error",
          confirmButtonText: "OK"
        });
        return { isValid: false, hasApprovalHierarchy: false };
      }

      const reportData = reportValidation.data;
      const hasApprovalHierarchy = reportData?.extra?.approvalData && reportData.extra.approvalData.length > 0;
      return { isValid: true, hasApprovalHierarchy, reportData };
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: "Unable to validate the selected expense report. Please try selecting a different report.",
        icon: "error",
        confirmButtonText: "OK"
      });
      return { isValid: false, hasApprovalHierarchy: false };
    }
  };

  const validateEditModeChanges = (values: any, isEditMode: boolean) => {
    if (isEditMode) {
      const { dirtyFields } = methods.formState;
      const dirtyData = getDirtyFieldsOnly(values, dirtyFields) || {};

      // Detect itemized changes (add/update/delete) independent of form dirty fields
      const originalItems = (location.state?.expenseData?.items || [])
        .filter((i: any) => i && (i.itemId || i.itemId === 0));

      const currentItems = (itemizedExpenses || [])
        .filter((e) => e && e.category && e.description && ((typeof e.amount === 'string' ? parseFloat(e.amount) : e.amount) || 0) > 0)
        .map((e) => {
          const amountNum = typeof e.amount === 'string' ? parseFloat(e.amount) || 0 : e.amount || 0;
          let taxPercent = 0;
          if (e.tax_percentage) {
            taxPercent = e.tax_percentage.toString().includes('%') ? parseFloat(e.tax_percentage.toString().replace('%', '')) || 0 : parseFloat(e.tax_percentage as any) || 0;
          }
          const mapped: any = {
            category: e.category,
            itemName: e.itemName,
            amount: amountNum.toString(),
            description: e.description,
            taxPercent: selectedTaxType === 'GST Applicable' ? taxPercent : 0,
          };
          if (goodsOrService === 'Goods' && e.hsnSac) mapped.hsnCode = e.hsnSac; else mapped.hsnCode = undefined;
          if (goodsOrService === 'Service' && e.hsnSac) mapped.sacCode = e.hsnSac; else mapped.sacCode = undefined;
          if (e.itemId !== undefined && e.itemId !== null) mapped.itemId = e.itemId;
          return mapped;
        });

      const currentIds = currentItems
        .map((ci: any) => ci.itemId)
        .filter((id) => id !== undefined && id !== null && id !== '');
      const originalIds = originalItems
        .map((oi: any) => oi.itemId)
        .filter((id: any) => id !== undefined && id !== null && id !== '');

      let hasItemChanges = false;
      // Any new items (no itemId)
      if (currentItems.some((ci: any) => ci.itemId === undefined || ci.itemId === null || ci.itemId === '')) {
        hasItemChanges = true;
      }
      // Any deletions
      if (!hasItemChanges && originalIds.some((id: any) => !currentIds.includes(id))) {
        hasItemChanges = true;
      }
      // Any updates to existing items
      if (!hasItemChanges) {
        const originalById: Record<string, any> = {};
        originalItems.forEach((oi: any) => {
          originalById[String(oi.itemId)] = oi;
        });
        for (const ci of currentItems) {
          if (ci.itemId !== undefined && ci.itemId !== null) {
            const oi = originalById[String(ci.itemId)];
            if (oi) {
              const origAmount = (oi.amountWithoutTax || oi.amount || 0).toString();
              const origTaxPercent = selectedTaxType === 'GST Applicable' ? (oi.taxPercent ?? oi.tax_percentage ?? 0) : 0;
              const origHsn = oi.hsnCode;
              const origSac = oi.sacCode;
              if (
                (oi.category || '') !== (ci.category || '') ||
                (oi.itemName || '') !== (ci.itemName || '') ||
                (oi.description || '') !== (ci.description || '') ||
                (origAmount || '0') !== (ci.amount || '0') ||
                (origTaxPercent || 0) !== (ci.taxPercent || 0) ||
                (goodsOrService === 'Goods' ? (origHsn || '') !== (ci.hsnCode || '') : false) ||
                (goodsOrService === 'Service' ? (origSac || '') !== (ci.sacCode || '') : false)
              ) {
                hasItemChanges = true;
                break;
              }
            }
          }
        }
      }

      if (Object.keys(dirtyData).length === 0 && !hasItemChanges) {
        Swal.fire({
          title: "No Changes",
          text: "No changes were made to the expense",
          icon: "info",
          confirmButtonText: "OK"
        });
        return { hasChanges: false, dirtyData: {} };
      }
      return { hasChanges: true, dirtyData };
    }
    return { hasChanges: true, dirtyData: {} };
  };

  // Form Submission: Handle form submission with validation and API calls
  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      // Check if we're in edit mode
      const { expenseId } = location.state || {};
      const isEditMode = expenseId && expenseId !== null;

      // Validate basic fields
      if (!validateBasicFields(values)) {
        setLoading(false);
        return;
      }

      // Validate report access
      const reportValidation = await validateReportAccess(values.report);
      if (!reportValidation.isValid) {
        setLoading(false);
        return;
      }

      // Validate GSTIN
      if (!validateGSTINField(values)) {
        setLoading(false);
        return;
      }

      // Validate amounts
      if (!validateAmountFields(values)) {
        setLoading(false);
        return;
      }

      // Validate destination of supply
      if (!validateDestinationOfSupply(values)) {
        setLoading(false);
        return;
      }

      // Validate edit mode changes
      const editValidation = validateEditModeChanges(values, isEditMode);
      if (!editValidation.hasChanges) {
        setLoading(false);
        return;
      }

      // Data preparation functions
      const prepareSingleExpenseData = (values: any, hasApprovalHierarchy: boolean, isEditMode: boolean, dirtyData: any) => {
        const mainAmount = parseFloat(values.amount) || 0;
        const itemizedTotal = calculateItemizedTotal();
        const finalAmount = isItemized && mainAmount === 0 ? itemizedTotal : mainAmount;
        const baseAmount = selectedTaxType === 'GST Applicable' && !isItemized ?
          (watchedAmount ? parseFloat(watchedAmount) : 0) : finalAmount;

        const expenseData: any = {
          expenseReportId: parseInt(values.report),
          projectId: parseInt(projectId?.toString() || '0'),
          userId: (currentUser?.genericRole?.toLowerCase && currentUser.genericRole.toLowerCase() === 'admin')
            ? (values.userId ? parseInt(values.userId) : currentUser?.id)
             : (currentUser?.id),
          merchant: values.merchant.trim(),
          productCategory: goodsOrService,
          destinationOfSupply: goodsOrService === 'Goods' ? (values.destinationOfSupply3 || "") : "",
          gstin: selectedTaxType !== 'GST Not Applicable' ? (values.gstin2 || "") : "",
          actionDate: values.expenseDate ? new Date(values.expenseDate).toISOString().replace('T', ' ').substring(0, 19) : new Date().toISOString().replace('T', ' ').substring(0, 19),
          amount: baseAmount,
          description: values.description || "",
          paymentMode: values.paymentMode,
          claimReimbursement: values.reimbursable || false,
          taxType: values.taxType,
          currencyId: values.currencyId || 1,
          referenceId: values.reference.trim(),
          status: hasApprovalHierarchy ? 'DRAFT' : 'APPROVED',
          itemSplit: !!isItemized,
          items: isItemized ? itemizedExpenses
            .filter(expense => expense.category && expense.itemName && expense.description && expense.amount > 0)
            .map(expense => {
              const item: any = {
                category: expense.category,
                itemName: expense.itemName,
                amount: expense.amount.toString(),
                description: expense.description,
                taxPercent: selectedTaxType === 'GST Applicable' ?
                  (expense.tax_percentage ? parseFloat(expense.tax_percentage.replace('%', '')) : 0) : 0,
                hsnCode: goodsOrService === 'Goods' ? (expense.hsnSac || "") : "",
                sacCode: goodsOrService === 'Service' ? (expense.hsnSac || "") : ""
              };
              // Only include itemId for existing items (edit mode); skip for new lines being created
              if (expense.itemId !== null && expense.itemId !== undefined) {
                item.itemId = expense.itemId;
              }
              return item;
            }) : []
        };

        // Header-level tax/HSN/SAC apply only in non-itemized mode; in item split mode the values live on each item.
        if (!isItemized) {
          expenseData.taxPercent = selectedTaxType === 'GST Applicable' ? (values.tax_percentage ? parseFloat(values.tax_percentage) : 0) : 0;
          if (goodsOrService === 'Goods') {
            expenseData.hsnCode = values.hsnSac || "";
          } else if (goodsOrService === 'Service') {
            expenseData.sacCode = values.hsnSac || "";
          }
        }

        if (values.paymentMode === 'Cheque') {
          expenseData.chequeNumber = values.chequeNumber;
          expenseData.chequeDate = values.chequeDate ? dayjs(values.chequeDate).format('YYYY-MM-DD') : null;
        }

        if (isEditMode) {
          const { expenseId } = location.state || {};
          expenseData.expenseId = parseInt(expenseId);
          
          const filteredExpenseData: any = { 
            expenseId: parseInt(expenseId),
            projectId: parseInt(projectId?.toString() || '0')
          };
          const dirtyKeys = Object.keys(dirtyData || {});
          const includeIfPresent = (key: string) => {
            if (expenseData.hasOwnProperty(key)) {
              filteredExpenseData[key] = expenseData[key];
            }
          };
          dirtyKeys.forEach((key) => {
            switch (key) {
              case 'reference':
                includeIfPresent('referenceId');
                break;
              case 'expenseDate':
                includeIfPresent('actionDate');
                break;
              case 'gstin2':
                includeIfPresent('gstin');
                break;
              case 'hsnSac':
                // Only relevant when not itemized
                if (!isItemized) {
                  if (goodsOrService === 'Goods') includeIfPresent('hsnCode');
                  if (goodsOrService === 'Service') includeIfPresent('sacCode');
                }
                break;
              case 'destinationOfSupply3':
                includeIfPresent('destinationOfSupply');
                break;
              case 'report':
                includeIfPresent('expenseReportId');
                break;
              case 'reimbursable':
                includeIfPresent('claimReimbursement');
                break;
              default:
                includeIfPresent(key);
            }
          });
          // Always send current item split state for updates
          filteredExpenseData.itemSplit = !!isItemized;

          // Always send cheque fields when payment mode is Cheque, even if dirty tracking missed them
          if (values.paymentMode === 'Cheque') {
            filteredExpenseData.paymentMode = 'Cheque';
            filteredExpenseData.chequeNumber = expenseData.chequeNumber;
            filteredExpenseData.chequeDate = expenseData.chequeDate;
          }

          return { expenseData: filteredExpenseData, isEditMode: true };
        }

        return { expenseData, isEditMode: false };
      };



      const handleItemsForEditMode = (expenseData: any, isEditMode: boolean) => {
        if (!isEditMode) {
          return expenseData;
        }

        // Existing items in the original expense (from backend)
        const originalItems = (location.state?.expenseData?.items || []).filter((i: any) => i && (i.itemId || i.itemId === 0));

        // If item split is OFF now, remove all existing line items in backend by sending only deletions
        if (!isItemized) {
          if (originalItems.length > 0) {
            expenseData.items = originalItems
              .filter((orig: any) => orig.itemId !== undefined && orig.itemId !== null)
              .map((orig: any) => ({ itemId: orig.itemId, deleteItem: true }));
          } else {
            delete expenseData.items;
          }
          return expenseData;
        }

        // Item split is ON: build flat items array from current UI state
        const currentItems = (itemizedExpenses || [])
          .filter((e) => e && e.category && e.description && ((typeof e.amount === 'string' ? parseFloat(e.amount) : e.amount) || 0) > 0)
          .map((e) => {
            const amountNum = typeof e.amount === 'string' ? parseFloat(e.amount) || 0 : e.amount || 0;
            let taxPercent = 0;
            if (e.tax_percentage) {
              taxPercent = e.tax_percentage.toString().includes('%') ? parseFloat(e.tax_percentage.toString().replace('%', '')) || 0 : parseFloat(e.tax_percentage as any) || 0;
            }
            const mapped: any = {
              category: e.category,
              itemName: e.itemName,
              amount: amountNum.toString(),
              description: e.description,
              taxPercent: selectedTaxType === 'GST Applicable' ? taxPercent : 0,
            };
            if (goodsOrService === 'Goods' && e.hsnSac) mapped.hsnCode = e.hsnSac;
            if (goodsOrService === 'Service' && e.hsnSac) mapped.sacCode = e.hsnSac;
            if (e.itemId !== undefined && e.itemId !== null) mapped.itemId = e.itemId;
            return mapped;
          });

        const itemsPayload: any[] = [];

        // Add current items (both new and existing)
        currentItems.forEach((item: any) => {
          itemsPayload.push(item);
        });

        // Detect deleted items by comparing originals not found in current
        const currentIds = currentItems
          .map((ci: any) => ci.itemId)
          .filter((id) => id !== undefined && id !== null && id !== '');
        originalItems.forEach((orig: any) => {
          if (orig.itemId !== undefined && orig.itemId !== null && !currentIds.includes(orig.itemId)) {
            itemsPayload.push({ itemId: orig.itemId, deleteItem: true });
          }
        });

        if (itemsPayload.length > 0) {
          expenseData.items = itemsPayload;
        } else {
          delete expenseData.items;
        }
        return expenseData;
      };

      // Create FormData to handle consistent payload format
      const formDataToSend = new FormData();

      // Prepare the expense data
      const expenseDataResult = prepareSingleExpenseData(values, reportValidation.hasApprovalHierarchy, isEditMode, editValidation.dirtyData);
      let expenseData = expenseDataResult.expenseData;

      // Handle items for edit mode
      expenseData = handleItemsForEditMode(expenseData, isEditMode);

      // File handling functions
      const handleFileUploads = (formDataToSend: FormData, isEditMode: boolean) => {
        const filesToUpload = isEditMode ? 
          uploadedFiles.filter(file => !(file as any).isExisting) : 
          uploadedFiles;

        // Add fileData metadata to expenseData for create mode
        if (!isEditMode && filesToUpload.length > 0) {
          (expenseData as any).fileData = filesToUpload.map((file, index) => ({
            order: index,
            module: "expenses",
            moduleFileType: "payment_out"
          }));
        }

        if (filesToUpload.length > 0) {
          filesToUpload.forEach((file) => {
            try {
              // Check file size (10MB limit)
              if (file.size > 10 * 1024 * 1024) {
                Swal.fire({
                  icon: "error",
                  title: "File Size Error",
                  text: `File ${file.name} is too large. Maximum size is 10MB.`,
                  confirmButtonText: "Ok",
                });
                return;
              }

              // Append file to FormData directly
              formDataToSend.append("files", file, file.name);
            } catch (fileError) {
              Swal.fire({
                icon: "error",
                title: "File Processing Error",
                text: `Error processing file ${file?.name || "unknown"}. Please try again.`,
                confirmButtonText: "Ok",
              });
              return;
            }
          });
        }
      };

      const handleEditModeFileData = (expenseData: any, isEditMode: boolean) => {
        if (!isEditMode) return expenseData;

        const existingFiles = uploadedFiles.filter(file => (file as any).isExisting);
        const newFiles = uploadedFiles.filter(file => !(file as any).isExisting);
        
        const originalDmsFiles = location.state?.expenseData?.dms || [];
        const originalFileCount = originalDmsFiles.length;
        const currentExistingFileCount = existingFiles.length;
        
        const fileDataEntry: any = {};

        // Only add docKeys if there are new files
        if (newFiles.length > 0) {
          fileDataEntry.docKeys = newFiles.map((file, index) => ({
            order: index,
            module: "expenses",
            moduleFileType: "payment_out"
          }));
        }

        // Add deletedDocs if files were removed
        if (originalFileCount > currentExistingFileCount) {
          const currentDocIds: number[] = existingFiles
            .map(file => (file as any).docId)
            .filter((docId: any) => docId !== null && docId !== undefined);
          const originalDocIds: number[] = originalDmsFiles
            .map((dms: any) => dms.docId)
            .filter((docId: any) => docId !== null && docId !== undefined);

          const deletedDocIds: number[] = originalDocIds.filter((docId: number) => !currentDocIds.includes(docId));
          if (deletedDocIds.length > 0) {
            fileDataEntry.deletedDocs = deletedDocIds.map((docId) => ({ docId }));
          }
        }

        if (Object.keys(fileDataEntry).length > 0) {
          // Backend expects an array of fileData objects
          expenseData.fileData = [fileDataEntry];
        }

        return expenseData;
      };

      // Handle file uploads
      handleFileUploads(formDataToSend, isEditMode);
      
      // Handle edit mode file data
      expenseData = handleEditModeFileData(expenseData, isEditMode);

      // API call and response handling functions
      const handleApiCall = async (formDataToSend: FormData, isEditMode: boolean) => {
        if (isEditMode) {
          return await expensesApis.updatePaymentOut(formDataToSend);
        } else {
          return await expensesApis.createPaymentOut(formDataToSend);
        }
      };

      const handleSuccessResponse = async (response: any, values: any, isEditMode: boolean) => {
        // Create expense approval if this is a new expense
        if (!isEditMode && response.data?.expenseId) {
          try {
            await expensesApis.createExpenseApprovals({
              reportId: parseInt(values.report),
              expenseId: response.data.expenseId
            });
          } catch (approvalError) {
            // Don't show error to user as the expense was created successfully
          }
        }

        Swal.fire({
          title: "Success!",
          text: isEditMode ? "Expense updated successfully!" : "Expense submitted successfully!",
          icon: "success",
          confirmButtonText: "OK"
        }).then(async () => {
          // Navigate to report breakdown page after successful operations (create, edit, delete, view)
          if (values.report) {
            // Navigate to reports page with report breakdown modal open (never null in URL)
            const reportsPath = projectId != null && !isNaN(Number(projectId)) ? `/pms/expenses/reports/${projectId}` : '/pms/expenses/reports';
            navigate(reportsPath, {
              state: {
                projectId: projectId,
                reportId: values.report,
                reportName: reportOptions.find(option => option.value === values.report)?.label || 'Report',
                openReportBreakDown: true,
                refreshReportBreakDown: true
              }
            });
          } else {
            // Reset form state after successful creation (for edit mode or when no report selected)
            if (!isEditMode) {
              resetFormAfterSuccess();
            }
          }
        });
      };

      const resetFormAfterSuccess = () => {
        // Set flag to prevent API calls during form reset
        setIsFormResetting(true);
        
        // Reset form fields
        methods.reset({
          reimbursable: true,
          report: '',
          expenseDate: dayjs(),
          merchant: '',
          gstin2: '',
          paymentMode: '',
          amount: '',
          reference: '',
          description: '',
          taxType: 'GST Not Applicable',
          tax_percentage: '',
          hsnSac: '',
          destinationOfSupply3: '',
          chequeNumber: '',
          chequeDate: undefined,
          currency: 'INR',
          currencyId: 1,
          userId: '',
        });
        setUploadedFiles([]);
        setSelectedTaxType('GST Not Applicable');
        setItemizedExpenses([{ id: 1, category: '', itemName: '', description: '', amount: 0, type: 'Service', tax_percentage: '', hsnSac: '', cgst: 0, sgst: 0, igst: 0, totalTax: 0, totalAmount: 0, itemId: null }]);
        setExpenseTotalAmountFromApi(null);
        setExpenseSubtotalFromApi(null);
        setExpenseTotalTaxFromApi(null);
        // Reset category state
        setSelectedCategory('');
        setCustomCategoryName('');
        // Clear the report field to make it selectable again
        methods.setValue('report', '');
        // Clear validation errors
        setGstinValidationError('');
        
        // Reset the flag after form reset is complete
        setTimeout(() => {
          setIsFormResetting(false);
        }, 100);
      };

      const handleErrorResponse = (response: any) => {
        let errorMessage = "Failed to submit expense";

        if (response?.message) {
          if (response.message.includes("reportUserStatus")) {
            errorMessage = "The selected expense report is not accessible or has been deleted. Please select a different report.";
          } else if (response.message.includes("gRPC Error")) {
            errorMessage = "Server error occurred. Please try again or contact support if the issue persists.";
          } else {
            errorMessage = response.message;
          }
        }

        throw new Error(errorMessage);
      };


      // Always send expense data as JSON string in FormData
      formDataToSend.append("data", JSON.stringify(expenseData));

      // Call appropriate API based on mode
      const response = await handleApiCall(formDataToSend, isEditMode);

      if (response && response.status) {
        await handleSuccessResponse(response, values, isEditMode);
      } else {
        handleErrorResponse(response);
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to submit expense";

      Swal.fire({
        title: "Error!",
        text: errorMessage,
        icon: "error",
        confirmButtonText: "OK"
      }).then(() => {
        // Refresh reports list if there was a report access error
        if (errorMessage && (errorMessage.includes("report") || errorMessage.includes("Report"))) {
          fetchReportsData();
        }
      });
    } finally {
      setLoading(false);
    }
  };


  // OCR: Apply extracted OCR data to form fields
  const applyOCRDataToForm = (data: ReceiptOCRData, fileIndex?: number) => {
    if (!data) return;

    // Build confirmation dialog HTML
    let dialogHtml = `
      <div style="text-align: left; padding: 10px;">
        <p><strong>Merchant:</strong> ${data.merchant || 'N/A'}</p>
        <p><strong>Amount:</strong> ₹${data.amount || 'N/A'}</p>
        <p><strong>Date:</strong> ${data.date || data.actionDate || 'N/A'}</p>
        <p><strong>GSTIN:</strong> ${data.gstin || 'N/A'}</p>
        <p><strong>Tax %:</strong> ${data.tax_percentage || 'N/A'}%</p>
        <p><strong>Reference:</strong> ${data.reference || data.referenceId || 'N/A'}</p>
        ${data.destinationOfSupply || data.destinationOfSupply3 ? `<p><strong>Destination of Supply:</strong> ${data.destinationOfSupply || data.destinationOfSupply3}</p>` : ''}
    `;

    // Add itemized expenses info if available
    const items = data.items || data.itemizedExpenses;
    if (data.hasItemizedData && items && items.length > 0) {
      dialogHtml += `<p><strong>Itemized Items:</strong> ${items.length} items found</p>`;
      dialogHtml += `<div style="max-height: 150px; overflow-y: auto; margin-top: 10px; padding: 8px; background: #f8f9fa; border-radius: 4px;">`;
      items.forEach((item: any, index: number) => {
        const itemAmount = typeof item.amount === 'string' ? parseFloat(item.amount) : item.amount || 0;
        dialogHtml += `<p style="margin: 4px 0; font-size: 12px;"><strong>Item ${index + 1}:</strong> ${item.category || 'N/A'} - ${item.description || 'N/A'} (₹${itemAmount.toFixed(2)})</p>`;
      });
      dialogHtml += `</div>`;
    }

    dialogHtml += `</div>`;

    // Show confirmation dialog before applying OCR data
    Swal.fire({
      title: "OCR Data Extracted",
      html: dialogHtml,
      icon: "info",
      showCancelButton: true,
      confirmButtonColor: "#2557d6",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Apply to Form",
      cancelButtonText: "Cancel",
      customClass: {
        popup: 'swal2-popup-custom'
      },
      width: '600px'
    }).then((result) => {
      if (result.isConfirmed && data) {
        // Save original form values before applying OCR (only if not already saved)
        if (originalFormValues === null) {
          const currentValues = methods.getValues();
          setOriginalFormValues({
            merchant: currentValues.merchant || '',
            amount: currentValues.amount || '',
            expenseDate: currentValues.expenseDate || dayjs(),
            gstin2: currentValues.gstin2 || '',
            taxType: currentValues.taxType || 'GST Not Applicable',
            tax_percentage: currentValues.tax_percentage || '',
            hsnSac: currentValues.hsnSac || '',
            destinationOfSupply3: currentValues.destinationOfSupply3 || '',
            reference: currentValues.reference || '',
            description: currentValues.description || '',
            paymentMode: currentValues.paymentMode || '',
            currency: currentValues.currency || 'INR',
            currencyId: currentValues.currencyId || 1,
            reimbursable: currentValues.reimbursable !== undefined ? currentValues.reimbursable : true,
          });
          setOriginalItemizedExpenses([...itemizedExpenses]);
          setOriginalTaxType(selectedTaxType);
          setOriginalGoodsOrService(goodsOrService);
          
          // Track which file triggered OCR
          if (fileIndex !== undefined) {
            setOcrProcessedFileIndex(fileIndex);
          }
        }
        // Apply merchant name - replace any existing value
        if (data.merchant) {
          methods.setValue('merchant', data.merchant);
        }

        // Apply amount - replace any existing value (PDF/API total; no frontend calculation)
        const amountValue = typeof data.amount === 'number' ? data.amount.toString() : data.amount;
        if (amountValue) {
          methods.setValue('amount', amountValue);
          handleMainAmountChange(String(amountValue));
          const amountNum = typeof data.amount === 'number' ? data.amount : (data.amount != null ? parseFloat(String(data.amount)) : NaN);
          if (!Number.isNaN(amountNum)) setExpenseTotalAmountFromApi(amountNum);
        }
        // Apply PDF/OCR totals to summary - no frontend calculation
        if (data.subtotal != null) {
          const n = typeof data.subtotal === 'number' ? data.subtotal : parseFloat(String(data.subtotal));
          if (!Number.isNaN(n)) setExpenseSubtotalFromApi(n);
        }
        if (data.totalTax != null) {
          const n = typeof data.totalTax === 'number' ? data.totalTax : parseFloat(String(data.totalTax));
          if (!Number.isNaN(n)) setExpenseTotalTaxFromApi(n);
        } else if (data.taxAmount != null) {
          const n = typeof data.taxAmount === 'number' ? data.taxAmount : parseFloat(String(data.taxAmount));
          if (!Number.isNaN(n)) setExpenseTotalTaxFromApi(n);
        }

        // Apply date - replace any existing value
        if (data.actionDate) {
          try {
            const parsedDate = dayjs(data.actionDate);
            if (parsedDate.isValid()) {
              methods.setValue('expenseDate', parsedDate);
            }
          } catch (error) {
          }
        } else if (data.date) {
          try {
            const [day, month, year] = data.date.split('/');
            const parsedDate = dayjs(`${year}-${month}-${day}`);
            if (parsedDate.isValid()) {
              methods.setValue('expenseDate', parsedDate);
            }
          } catch (error) {
          }
        }

        // Apply tax type - replace any existing value
        if (data.taxType) {
          setSelectedTaxType(data.taxType);
          methods.setValue('taxType', data.taxType);
        }

        // Apply GSTIN - replace any existing value
        if (data.gstin) {
          methods.setValue('gstin2', data.gstin);
          validateGSTIN(data.gstin);
        }

        // Apply tax percentage if available - replace any existing value
        const taxPercentValue = typeof data.tax_percentage === 'number' ? data.tax_percentage.toString() : data.tax_percentage;
        if (taxPercentValue) {
          if (data.taxType === 'GST Applicable' || data.gstin) {
            setSelectedTaxType('GST Applicable');
            methods.setValue('taxType', 'GST Applicable');
          }
          methods.setValue('tax_percentage', taxPercentValue);
          handleHeaderTaxPercentChange(taxPercentValue);
        }

        // Apply HSN/SAC code - replace any existing value
        const hsnSacValue = data.hsnCode || data.sacCode || data.hsnSac;
        if (hsnSacValue) {
          methods.setValue('hsnSac', hsnSacValue);
        }

        // Apply product category (Goods or Service) - must be set before destination of supply
        if (data.productCategory) {
          setGoodsOrService(data.productCategory);
          
          // Apply destination of supply immediately if productCategory is Goods
          const destValue = data.destinationOfSupply || data.destinationOfSupply3;
          if (data.productCategory === 'Goods' && destValue) {
            methods.setValue('destinationOfSupply3', destValue);
          }
        } else {
          const destValue = data.destinationOfSupply || data.destinationOfSupply3;
          if (destValue && goodsOrService === 'Goods') {
            methods.setValue('destinationOfSupply3', destValue);
          }
        }

        // Apply reference - replace any existing value
        const referenceValue = data.referenceId || data.reference;
        if (referenceValue) {
          methods.setValue('reference', referenceValue);
        }

        // Apply description - replace any existing value
        if (data.description) {
          methods.setValue('description', data.description);
        }

        // Apply payment mode - replace any existing value
        if (data.paymentMode) {
          methods.setValue('paymentMode', data.paymentMode);
        }

        // Apply currency - replace any existing value
        if (data.currency) {
          methods.setValue('currency', data.currency);
        }

        // Apply currency ID - replace any existing value
        if (data.currencyId) {
          methods.setValue('currencyId', data.currencyId);
        }

        // Apply claim reimbursement - replace any existing value
        if (data.claimReimbursement !== undefined) {
          methods.setValue('reimbursable', data.claimReimbursement);
        }

        // Apply itemized expenses if available
        const items = data.items || data.itemizedExpenses;
        if (data.hasItemizedData && items && items.length > 0) {
          // Set tax type to GST Applicable if GSTIN is present or taxType is GST Applicable
          if (data.gstin || data.taxType === 'GST Applicable') {
            setSelectedTaxType('GST Applicable');
            methods.setValue('taxType', 'GST Applicable');
          }

          // Enable itemized mode
          if (!isItemized) {
            setIsItemized(true);
          }

          // Transform OCR itemized data to component format
          const transformedItems = items.map((item: any, index: number) => {
            const amount = typeof item.amount === 'string' ? parseFloat(item.amount) || 0 : item.amount || 0;
            const taxPercent = item.tax_percentage || (item.tax_percentage ? parseFloat(item.tax_percentage.toString().replace('%', '')) : 0);
            
            // Calculate tax amounts based on tax type
            let cgst = 0, sgst = 0, igst = 0, totalTax = 0;
            
            if ((data.gstin || data.taxType === 'GST Applicable') && taxPercent > 0) {
              totalTax = (amount * taxPercent) / 100;
              // For GST Applicable, split into CGST and SGST (50% each)
              cgst = totalTax / 2;
              sgst = totalTax / 2;
              igst = 0; // IGST is 0 for domestic transactions
            }
            
            const hsnSacValue = item.hsnCode || item.sacCode || item.hsnSac || '';
            
            return {
              id: index + 1,
              category: item.category || '',
              itemName: item.description || item.itemName || '', // API description maps to Item Name
              description: item.description || '',
              amount: amount,
              type: data.productCategory || goodsOrService,
              tax_percentage: taxPercent ? `${taxPercent}%` : '0%',
              hsnSac: hsnSacValue,
              cgst: cgst,
              sgst: sgst,
              igst: igst,
              totalTax: totalTax,
              totalAmount: amount + totalTax,
              itemId: null
            };
          });

          // Set itemized expenses
          setItemizedExpenses(transformedItems);

          // Clear main amount when using itemized mode
          methods.setValue('amount', '');
          setAmountValidationError('');
          setItemizedValidationError('');
        }

        Swal.fire({
          title: "Success!",
          text: data.hasItemizedData 
            ? `OCR data has been applied to the form with ${items?.length || 0} itemized items`
            : "OCR data has been applied to the form",
          icon: "success",
          confirmButtonText: "OK",
          timer: 2000
        });
      }
      setOcrData(null);
    });
  };

  // Helper function to check if file is OCR-processable
  const isOCRProcessableFile = (file: File): boolean => {
    const fileType = file.type.toLowerCase();
    const fileName = file.name.toLowerCase();
    
    // Check by MIME type
    if (fileType.startsWith('image/')) return true;
    if (fileType === 'application/pdf') return true;
    if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return true; // .docx
    if (fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return true; // .xlsx
    if (fileType === 'application/vnd.ms-excel') return true; // .xls
    if (fileType === 'text/csv') return true; // .csv
    
    // Fallback: check by file extension
    const validExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.docx', '.xlsx', '.xls', '.csv'];
    return validExtensions.some(ext => fileName.endsWith(ext));
  };

  // OCR: Process file for OCR extraction
  const processOCRForFile = async (file: File, fileIndex?: number) => {
    // Process all supported OCR file types
    if (!isOCRProcessableFile(file)) {
      return;
    }

    setProcessingOCR(true);
    try {
      // Get expenseId from location state (used as paymentId for OCR API)
      const { expenseId } = location.state || {};
      const paymentId = expenseId || 0; // Use 0 as default if not available
      
      const extractedData = await extractReceiptData(file, paymentId);
      if (extractedData) {
        setOcrData(extractedData);
        applyOCRDataToForm(extractedData, fileIndex);
      }
    } catch (error) {
    } finally {
      setProcessingOCR(false);
    }
  };

  // File Management: Handle file upload with image preview setup and OCR processing
  const handleFileUpload = (files: FileList | null) => {
    if (files) {
      const newFiles = Array.from(files);
      
      // Validate file size for OCR-processable files - must be less than 10MB
      const maxFileSize = 10 * 1024 * 1024; // 10MB in bytes
      const ocrProcessableFiles = newFiles.filter(file => isOCRProcessableFile(file));
      
      // Check if any OCR-processable file exceeds 10MB
      const oversizedFiles = ocrProcessableFiles.filter(file => file.size > maxFileSize);
      if (oversizedFiles.length > 0) {
        const fileNames = oversizedFiles.map(file => file.name).join(', ');
        Swal.fire({
          icon: "error",
          title: "File Size Error",
          text: `The following file(s) exceed the 10MB limit for OCR processing: ${fileNames}. Please upload files smaller than 10MB.`,
          confirmButtonText: "Ok",
        });
        return; // Don't process files if any exceed the limit
      }
      
      const processedFiles = newFiles.map(file => {
        // Mark new files as not existing
        (file as any).isExisting = false;
        return file;
      });
      
      setUploadedFiles(prev => {
        const updatedFiles = [...prev, ...processedFiles];
        // If this is the first image being added, set it as displayed
        if (prev.filter(file => file.type.startsWith('image/')).length === 0 && 
            processedFiles.some(file => file.type.startsWith('image/'))) {
          setDisplayedImageIndex(0);
        }
        return updatedFiles;
      });

      // Process OCR for the first OCR-processable file (if not in view mode)
      const { viewMode } = location.state || {};
      if (!viewMode && processedFiles.length > 0) {
        // Process the first OCR-processable file for OCR
        const firstProcessableFileIndex = processedFiles.findIndex(file => isOCRProcessableFile(file));
        if (firstProcessableFileIndex >= 0) {
          const firstProcessableFile = processedFiles[firstProcessableFileIndex];
          const globalFileIndex = uploadedFiles.length + firstProcessableFileIndex;
          processOCRForFile(firstProcessableFile, globalFileIndex);
        }

        // Load PDF pages for newly uploaded PDFs
        processedFiles.forEach((file, fileIndex) => {
          if (file.type === 'application/pdf') {
            const globalIndex = uploadedFiles.length + fileIndex;
            loadPdfPages(file, globalIndex);
          }
        });
      }
    }
  };

  // Load PDF pages when PDFs are uploaded
  useEffect(() => {
    const pdfFiles = getPdfFiles();
    pdfFiles.forEach((file, index) => {
      if (!pdfPageNumbers[index] && !loadingPdfPages[index]) {
        setLoadingPdfPages(prev => ({ ...prev, [index]: true }));
        loadPdfPages(file, index).finally(() => {
          setLoadingPdfPages(prev => ({ ...prev, [index]: false }));
        });
      }
    });
  }, [uploadedFiles]);

  // Render PDF page when page changes
  useEffect(() => {
    const currentPdf = getCurrentDisplayedPdf();
    if (currentPdf) {
      const pdfIndex = getPdfFiles().findIndex(f => f === currentPdf);
      if (pdfIndex >= 0) {
        const pageNumber = currentPdfPage[pdfIndex] || 1;
        const imageKey = `${pdfIndex}-${pageNumber}`;
        
        if (!pdfPageImages[imageKey] && !loadingPdfPages[pdfIndex] && pdfPageNumbers[pdfIndex]) {
          setLoadingPdfPages(prev => ({ ...prev, [pdfIndex]: true }));
          renderPdfPage(currentPdf, pageNumber, pdfIndex).then((imageData) => {
            if (imageData) {
              setPdfPageImages(prev => ({
                ...prev,
                [imageKey]: imageData
              }));
            }
            setLoadingPdfPages(prev => ({ ...prev, [pdfIndex]: false }));
          }).catch(() => {
            setLoadingPdfPages(prev => ({ ...prev, [pdfIndex]: false }));
          });
        }
      }
    }
  }, [displayedPdfIndex, currentPdfPage, uploadedFiles, pdfPageNumbers]);

  // Drag and Drop: Handle drag over event for file upload
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  // Drag and Drop: Handle drag leave event for file upload
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  // Drag and Drop: Handle file drop event for file upload
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const removeFile = (index: number) => {
    const fileToRemove = uploadedFiles[index];
    const fileName = fileToRemove?.name || 'file';
    
    Swal.fire({
      title: "Are you sure?",
      text: `You want to delete "${fileName}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#2557d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel"
    }).then((result) => {
      if (result.isConfirmed) {
        setUploadedFiles(prev => {
          const newFiles = prev.filter((_, i) => i !== index);
          
          const shouldResetFields = 
            newFiles.length === 0 ||
            (ocrProcessedFileIndex !== null && index === ocrProcessedFileIndex);
          
          if (shouldResetFields) {
            if (originalFormValues !== null) {
              methods.setValue('merchant', originalFormValues.merchant);
              methods.setValue('amount', originalFormValues.amount);
              methods.setValue('expenseDate', originalFormValues.expenseDate);
              methods.setValue('gstin2', originalFormValues.gstin2);
              methods.setValue('taxType', originalFormValues.taxType);
              methods.setValue('tax_percentage', originalFormValues.tax_percentage);
              methods.setValue('hsnSac', originalFormValues.hsnSac);
              methods.setValue('destinationOfSupply3', originalFormValues.destinationOfSupply3);
              methods.setValue('reference', originalFormValues.reference);
              methods.setValue('description', originalFormValues.description);
              methods.setValue('paymentMode', originalFormValues.paymentMode);
              methods.setValue('currency', originalFormValues.currency);
              methods.setValue('currencyId', originalFormValues.currencyId);
              methods.setValue('reimbursable', originalFormValues.reimbursable);
              
              if (originalItemizedExpenses !== null) {
                setItemizedExpenses([...originalItemizedExpenses]);
              } else {
                setItemizedExpenses([{ id: 1, category: '', itemName: '', description: '', amount: 0, type: 'Service', tax_percentage: '', hsnSac: '', cgst: 0, sgst: 0, igst: 0, totalTax: 0, totalAmount: 0, itemId: null }]);
              }
              
              setSelectedTaxType(originalTaxType);
              setGoodsOrService(originalGoodsOrService);
            } else {
              if (ocrData !== null || ocrProcessedFileIndex !== null) {
                methods.setValue('merchant', '');
                methods.setValue('amount', '');
                methods.setValue('expenseDate', dayjs());
                methods.setValue('gstin2', '');
                methods.setValue('taxType', 'GST Not Applicable');
                methods.setValue('tax_percentage', '');
                methods.setValue('hsnSac', '');
                methods.setValue('destinationOfSupply3', '');
                methods.setValue('reference', '');
                methods.setValue('description', '');
                methods.setValue('paymentMode', '');
                methods.setValue('currency', 'INR');
                methods.setValue('currencyId', 1);
                methods.setValue('reimbursable', true);
                
                setItemizedExpenses([{ id: 1, category: '', itemName: '', description: '', amount: 0, type: 'Service', tax_percentage: '', hsnSac: '', cgst: 0, sgst: 0, igst: 0, totalTax: 0, totalAmount: 0, itemId: null }]);
                
                setSelectedTaxType('GST Not Applicable');
                setGoodsOrService('Service');
                setSelectedCategory('');
                setCustomCategoryName('');
              }
            }
            
            setHeaderTaxAmount(0);
            setHeaderTotalAmount(0);
            setAmountValidationError('');
            setItemizedValidationError('');
            setGstinValidationError('');
            setOriginalFormValues(null);
            setOriginalItemizedExpenses(null);
            setOcrProcessedFileIndex(null);
            setOcrData(null);
            
            if (newFiles.length === 0) {
              setIsItemized(false);
            }
          } else if (ocrProcessedFileIndex !== null && index < ocrProcessedFileIndex) {
            setOcrProcessedFileIndex(ocrProcessedFileIndex - 1);
          }
          
          if (displayedImageIndex >= newFiles.length && newFiles.length > 0) {
            setDisplayedImageIndex(newFiles.length - 1);
          } else if (newFiles.length === 0) {
            setDisplayedImageIndex(0);
          } else if (index < displayedImageIndex) {
            setDisplayedImageIndex(displayedImageIndex - 1);
          }
          
          setImageZoom(1);
          setIsImageZoomed(false);
          
          return newFiles;
        });
      }
    });
  };

  // Image Gallery: Get all uploaded image files
  const getImageFiles = () => {
    return uploadedFiles.filter(file => file.type.startsWith('image/'));
  };

  // PDF Gallery: Get all uploaded PDF files
  const getPdfFiles = () => {
    return uploadedFiles.filter(file => file.type === 'application/pdf');
  };

  // Image Gallery: Get currently displayed image
  const getCurrentDisplayedImage = () => {
    const imageFiles = getImageFiles();
    if (imageFiles.length === 0) return null;
    return imageFiles[displayedImageIndex] || imageFiles[0];
  };

  // PDF Gallery: Get currently displayed PDF
  const getCurrentDisplayedPdf = () => {
    const pdfFiles = getPdfFiles();
    if (pdfFiles.length === 0) return null;
    return pdfFiles[displayedPdfIndex] || pdfFiles[0];
  };

  // Image Gallery: Navigate to next image
  const showNextImage = () => {
    const imageFiles = getImageFiles();
    if (imageFiles.length > 1) {
      setDisplayedImageIndex((prev) => (prev + 1) % imageFiles.length);
      // Reset zoom when changing images
      setImageZoom(1);
      setIsImageZoomed(false);
    }
  };

  // Image Gallery: Navigate to previous image
  const showPreviousImage = () => {
    const imageFiles = getImageFiles();
    if (imageFiles.length > 1) {
      setDisplayedImageIndex((prev) => (prev - 1 + imageFiles.length) % imageFiles.length);
      // Reset zoom when changing images
      setImageZoom(1);
      setIsImageZoomed(false);
    }
  };

  // PDF Gallery: Navigate to next PDF
  const showNextPdf = () => {
    const pdfFiles = getPdfFiles();
    if (pdfFiles.length > 1) {
      setDisplayedPdfIndex((prev) => (prev + 1) % pdfFiles.length);
    }
  };

  // PDF Gallery: Navigate to previous PDF
  const showPreviousPdf = () => {
    const pdfFiles = getPdfFiles();
    if (pdfFiles.length > 1) {
      setDisplayedPdfIndex((prev) => (prev - 1 + pdfFiles.length) % pdfFiles.length);
    }
  };

  // PDF: Navigate to next page
  const showNextPdfPage = (pdfIndex: number) => {
    const currentPage = currentPdfPage[pdfIndex] || 1;
    const totalPages = pdfPageNumbers[pdfIndex] || 1;
    if (currentPage < totalPages) {
      setCurrentPdfPage(prev => ({
        ...prev,
        [pdfIndex]: currentPage + 1
      }));
    }
  };

  // PDF: Navigate to previous page
  const showPreviousPdfPage = (pdfIndex: number) => {
    const currentPage = currentPdfPage[pdfIndex] || 1;
    if (currentPage > 1) {
      setCurrentPdfPage(prev => ({
        ...prev,
        [pdfIndex]: currentPage - 1
      }));
    }
  };

  // PDF: Load PDF.js from CDN
  const loadPdfJs = (): Promise<any> => {
    return new Promise((resolve, reject) => {
      if ((window as any).pdfjsLib) {
        resolve((window as any).pdfjsLib);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
      script.onload = () => {
        (window as any).pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        resolve((window as any).pdfjsLib);
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  };

  // PDF: Load PDF and get page count
  const loadPdfPages = async (file: File, index: number) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdfjsLib = await loadPdfJs();
      
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;
      
      setPdfPageNumbers(prev => ({
        ...prev,
        [index]: pdf.numPages
      }));
      
      if (!currentPdfPage[index]) {
        setCurrentPdfPage(prev => ({
          ...prev,
          [index]: 1
        }));
      }
    } catch (error) {
      setPdfPageNumbers(prev => ({
        ...prev,
        [index]: 1
      }));
    }
  };

  // PDF: Render PDF page as image
  const renderPdfPage = async (file: File, pageNumber: number, index: number): Promise<string | null> => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdfjsLib = await loadPdfJs();
      
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(pageNumber);
      
      const viewport = page.getViewport({ scale: 2.0 });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      
      if (!context) return null;
      
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;
      
      return canvas.toDataURL('image/png');
    } catch (error) {
      return null;
    }
  };

  // Image Gallery: Zoom in on current image
  const handleZoomIn = () => {
    setImageZoom(prev => Math.min(prev + 0.5, 3));
    setIsImageZoomed(true);
  };

  // Image Gallery: Zoom out on current image
  const handleZoomOut = () => {
    setImageZoom(prev => {
      const newZoom = Math.max(prev - 0.5, 0.5);
      if (newZoom === 1) {
        setIsImageZoomed(false);
      }
      return newZoom;
    });
  };

  // Image Gallery: Reset zoom to default level
  const resetZoom = () => {
    setImageZoom(1);
    setIsImageZoomed(false);
  };

  // Image Gallery: Handle image click for zoom toggle
  const handleImageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isImageZoomed) {
      resetZoom();
    } else {
      handleZoomIn();
    }
  };

  // Navigation: Handle cancel action and navigate back
  const handleCancel = () => {
    // Reset form if using react-hook-form
    navigate(-1);
  };

  // Expense Management: Delete expense with confirmation
  const handleDeleteExpense = async (expenseId: string | number) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#2557d6",
      confirmButtonText: "Yes, delete it!"
    });

    if (result.isConfirmed) {
      setLoading(true);
      try {
        const response = await expensesApis.deleteExpense(expenseId);

        if (response && response.status) {
          Swal.fire("Deleted!", "Expense has been deleted.", "success");
          // Navigate to report breakdown page after successful deletion (never null in URL)
          if (selectedReport) {
            const reportsPath = projectId != null && !isNaN(Number(projectId)) ? `/pms/expenses/reports/${projectId}` : '/pms/expenses/reports';
            navigate(reportsPath, {
              state: {
                projectId: projectId,
                reportId: selectedReport,
                reportName: reportOptions.find(option => option.value === selectedReport)?.label || 'Report',
                openReportBreakDown: true,
                refreshReportBreakDown: true
              }
            });
          } else {
            // Navigate back or refresh the list
            navigate(-1);
          }
        } else {
          throw new Error(response?.message || "Failed to delete expense");
        }
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        Swal.fire({
          title: "Error!",
          text: errorMessage,
          icon: "error",
          confirmButtonText: "OK"
        });
      } finally {
        setLoading(false);
      }
    }
  };

  // Navigation: Handle back button navigation based on source
  const handleBack = () => {
    // Check if we came from ReportBreakDown and have a backUrl
    if (location.state?.fromReportBreakDown && location.state?.backUrl) {
      navigate(location.state.backUrl, {
        state: {
          projectId: location.state.projectId,
          reportId: location.state.reportId,
          reportName: location.state.reportName,
          openReportBreakDown: true, // Flag to automatically open the report breakdown modal
          refreshReportBreakDown: true // Flag to refresh the data in the modal
        }
      });
    } else {
      navigate(-1);
    }
  };



  // Itemized Expenses: Toggle between itemized and non-itemized modes
  const handleItemizeToggle = () => {
    const goingToItemized = !isItemized;

    // If switching to itemized mode, ensure we have at least 2 items
    if (goingToItemized) {
      // Check if we have at least 2 valid items
      const validItems = itemizedExpenses.filter(expense =>
        expense.category && expense.itemName && expense.description && expense.amount > 0
      );

      // If we don't have 2 valid items, add default empty items to make it 2
      if (validItems.length < 2) {
        const currentLength = itemizedExpenses.length;
        const itemsToAdd = 2 - currentLength;

        const newItems: Array<{
          id: number;
          category: string;
          itemName: string;
          description: string;
          amount: number;
          type: string;
          tax_percentage: string;
          hsnSac: string;
          cgst: number;
          sgst: number;
          igst: number;
          totalTax: number;
          totalAmount: number;
          itemId: number | null;
        }> = [];

        for (let i = 0; i < itemsToAdd; i++) {
          newItems.push({
            id: Math.max(...itemizedExpenses.map(e => e.id)) + i + 1,
            category: '',
            itemName: '',
            description: '',
            amount: 0,
            type: goodsOrService,
            tax_percentage: '',
            hsnSac: '',
            cgst: 0,
            sgst: 0,
            igst: 0,
            totalTax: 0,
            totalAmount: 0,
            itemId: null
          });
        }

        setItemizedExpenses(prev => [...prev, ...newItems]);
      }
    }

    setIsItemized(goingToItemized);

    if (goingToItemized) {
      // Clear main amount when switching to itemized mode to avoid confusion
      methods.setValue('amount', '');
      setAmountValidationError('');
      setItemizedValidationError('');
    } else {
      // Reset all line-level state when hiding the split
      methods.setValue('amount', '');
      methods.setValue('tax_percentage', '');
      methods.setValue('hsnSac', '');
      setItemizedExpenses([{
        id: 1,
        category: '',
        itemName: '',
        description: '',
        amount: 0,
        type: goodsOrService,
        tax_percentage: '',
        hsnSac: '',
        cgst: 0,
        sgst: 0,
        igst: 0,
        totalTax: 0,
        totalAmount: 0,
        itemId: null,
      }]);
      setExpenseTotalAmountFromApi(null);
      setExpenseSubtotalFromApi(null);
      setExpenseTotalTaxFromApi(null);
      setAmountValidationError('');
      setItemizedValidationError('');
    }

    // Reset tax calculations when switching modes
    setHeaderTaxAmount(0);
    setHeaderTotalAmount(0);
  };

  // Handle main amount field change and validate against itemized total
  const handleMainAmountChange = (value: string) => {
    const mainAmount = parseFloat(value) || 0;
    const itemizedTotal = calculateItemizedTotal();

    // Calculate header tax amounts when GST Applicable and not itemized
    if (!isItemized && selectedTaxType === 'GST Applicable') {
      const taxPercentValue = methods.getValues('tax_percentage');
      const taxPercent = parseFloat(taxPercentValue) || 0;
      const taxAmount = (mainAmount * taxPercent) / 100;
      setHeaderTaxAmount(taxAmount);
      setHeaderTotalAmount(mainAmount + taxAmount);
    } else {
      setHeaderTaxAmount(0);
      setHeaderTotalAmount(mainAmount);
    }

    // Clear any validation errors
    setAmountValidationError('');
  };

  // Handle tax percentage change for header-level calculation
  const handleHeaderTaxPercentChange = (value: string) => {
    const mainAmount = parseFloat(methods.getValues('amount')) || 0;
    const taxPercent = parseFloat(value) || 0;

    if (!isItemized && selectedTaxType === 'GST Applicable' && mainAmount > 0) {
      const taxAmount = (mainAmount * taxPercent) / 100;
      setHeaderTaxAmount(taxAmount);
      setHeaderTotalAmount(mainAmount + taxAmount);
    } else {
      setHeaderTaxAmount(0);
      setHeaderTotalAmount(mainAmount);
    }
  };

  // Update itemized expenses type when goodsOrService changes
  useEffect(() => {
    if (isItemized) {
      setItemizedExpenses(prev => prev.map(expense => ({
        ...expense,
        type: goodsOrService
      })));
    }
  }, [goodsOrService, isItemized]);

  // Recalculate tax amounts when tax type changes
  useEffect(() => {
    if (isItemized) {
      setItemizedExpenses(prev => prev.map(expense => {
        const amount = typeof expense.amount === 'string' ? parseFloat(expense.amount) || 0 : expense.amount || 0;
        
        // Calculate tax only when GST Applicable is selected and tax rate is provided
        if (selectedTaxType === 'GST Applicable' && amount > 0 && expense.tax_percentage && expense.tax_percentage !== '') {
          const taxAmounts = calculateTaxAmounts(amount, expense.tax_percentage);
          return {
            ...expense,
            cgst: taxAmounts.cgst,
            sgst: taxAmounts.sgst,
            igst: taxAmounts.igst,
            totalTax: taxAmounts.totalTax,
            totalAmount: amount + taxAmounts.totalTax
          };
        } else {
          // No tax calculation for GST Not Applicable, Outside scope, GST with Zero Percent
          return {
            ...expense,
            cgst: 0,
            sgst: 0,
            igst: 0,
            totalTax: 0,
            totalAmount: amount
          };
        }
      }));
    }
  }, [selectedTaxType, isItemized]);

  // Clear GSTIN field when tax type changes to "GST Not Applicable"
  useEffect(() => {
    if (selectedTaxType === 'GST Not Applicable') {
      methods.setValue('gstin2', '');
      setGstinValidationError(''); // Clear validation error
    }
  }, [selectedTaxType, methods]);

  // Watch for changes in amount and tax percentage to recalculate tax amounts
  const watchedAmount = methods.watch('amount');
  const watchedTaxPercent = methods.watch('tax_percentage');

  useEffect(() => {
    if (!isItemized && selectedTaxType === 'GST Applicable' && watchedAmount && watchedTaxPercent) {
      const mainAmount = parseFloat(watchedAmount) || 0;
      const taxPercent = parseFloat(watchedTaxPercent) || 0;

      if (mainAmount > 0 && taxPercent >= 0) {
        const taxAmount = (mainAmount * taxPercent) / 100;
        setHeaderTaxAmount(taxAmount);
        setHeaderTotalAmount(mainAmount + taxAmount);
      }
    } else if (!isItemized && selectedTaxType !== 'GST Applicable') {
      // Reset tax amounts for non-GST tax types
      setHeaderTaxAmount(0);
      setHeaderTotalAmount(parseFloat(watchedAmount) || 0);
    }
  }, [watchedAmount, watchedTaxPercent, selectedTaxType, isItemized]);

  // Recalculate form amount only in create mode; in edit mode Total Amount comes from API (expenseTotalAmountFromApi), no frontend calculation
  useEffect(() => {
    if (isItemized && expenseTotalAmountFromApi == null) {
      const filteredExpenses = itemizedExpenses.filter(expense => expense.type === goodsOrService);
      const totalAmount = filteredExpenses.reduce((total, expense) => {
        const amount = typeof expense.totalAmount === 'string' ? parseFloat(expense.totalAmount) || 0 : expense.totalAmount || 0;
        return total + amount;
      }, 0);
      methods.setValue('amount', totalAmount.toString());
    }
  }, [itemizedExpenses, goodsOrService, isItemized, expenseTotalAmountFromApi, methods]);

  // Itemized Expenses: Add new row to itemized expense table
  const addItemizedExpenseRow = () => {
    const newId = Math.max(...itemizedExpenses.map(e => e.id)) + 1;
    setItemizedExpenses(prev => {
      const updatedExpenses = [...prev, {
        id: newId,
        category: '',
        itemName: '',
        description: '',
        amount: 0,
        type: goodsOrService, // Use the currently selected goodsOrService type
        tax_percentage: '',
        hsnSac: '',
        cgst: 0,
        sgst: 0,
        igst: 0,
        totalTax: 0,
        totalAmount: 0,
        itemId: null // New items don't have itemId
      }];

      // Clear validation error when adding items
      setItemizedValidationError('');
      return updatedExpenses;
    });
  };

  // Itemized Expenses: Remove row from itemized expense table
  const removeItemizedExpenseRow = (id: number) => {
    if (itemizedExpenses.length > 1) {
      setItemizedExpenses(prev => {
        const updatedExpenses = prev.filter(expense => expense.id !== id);
        // Clear validation errors after removing a row
        setAmountValidationError('');

        // Check if we need to show validation error for minimum items
        const validItems = updatedExpenses.filter(expense =>
          expense.category && expense.itemName && expense.description && expense.amount > 0
        );
        if (validItems.length < 2) {
          setItemizedValidationError('Please add at least 2 valid items to use itemized mode');
        } else {
          setItemizedValidationError('');
        }

        return updatedExpenses;
      });
    }
  };

  // Itemized Expenses: Calculate total amount from all itemized expenses
  const calculateItemizedTotal = () => {
    return itemizedExpenses.reduce((total, expense) => {
      const amount = typeof expense.amount === 'string' ? parseFloat(expense.amount) || 0 : expense.amount || 0;
      return total + amount;
    }, 0);
  };

  // Validation: Validate GSTIN format
  const validateGSTIN = (gstin: string) => {
    if (!gstin || gstin.trim() === '') {
      setGstinValidationError('');
      return true;
    }

    // GSTIN format: 2 digits + 5 letters + 4 digits + 1 letter + 1 digit/letter + Z + 1 digit/letter
    const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
    
    if (!gstinRegex.test(gstin.toUpperCase())) {
      setGstinValidationError('Please enter a valid GSTIN format (e.g., 22AAAAA0000A1Z5)');
      return false;
    }

    setGstinValidationError('');
    return true;
  };

  // Watch GSTIN field for real-time validation
  const watchedGSTIN = methods.watch('gstin2');

  // Real-time GSTIN validation - validates as user types
  useEffect(() => {
    if (watchedGSTIN !== undefined) {
      validateGSTIN(watchedGSTIN);
    }
  }, [watchedGSTIN]);


  // Tax Calculation: Calculate CGST, SGST, IGST amounts based on tax rate
  const calculateTaxAmounts = (amount: number, taxRate: string) => {
    // For GST Not Applicable, Outside scope, GST with Zero Percent - no tax calculation
    if (selectedTaxType !== 'GST Applicable') {
      return { cgst: 0, sgst: 0, igst: 0, totalTax: 0 };
    }

    // Handle both percentage strings (e.g., "5%") and numeric strings (e.g., "5")
    let rate = 0;
    if (typeof taxRate === 'string') {
      if (taxRate.includes('%')) {
        rate = parseFloat(taxRate.replace('%', '')) / 100;
      } else {
        rate = parseFloat(taxRate) / 100;
      }
    } else {
      rate = parseFloat(taxRate) / 100;
    }
    
    const taxAmount = amount * rate;

    // For GST Applicable, split into CGST and SGST (50% each)
    const cgst = taxAmount / 2;
    const sgst = taxAmount / 2;
    const igst = 0; // IGST is 0 for domestic transactions
    return { cgst, sgst, igst, totalTax: taxAmount };
  };

  // Itemized Expenses: Handle field changes in itemized expense rows with tax calculation
  const handleItemizedExpenseChange = (id: number, field: string, value: any) => {
    setItemizedExpenses(prev => {
      const updatedExpenses = prev.map(expense => {
        if (expense.id === id) {
          const updatedExpense = { ...expense, [field]: value };

          // Calculate tax amounts when amount or tax rate changes
          if (field === 'amount' || field === 'tax_percentage') {
            const amount = field === 'amount' ? parseFloat(value) || 0 : (typeof expense.amount === 'string' ? parseFloat(expense.amount) : expense.amount) || 0;
            const taxRate = field === 'tax_percentage' ? value : expense.tax_percentage;

            // Calculate tax only when GST Applicable is selected and tax rate is provided
            if (selectedTaxType === 'GST Applicable' && amount > 0 && taxRate && taxRate !== '') {
              const taxAmounts = calculateTaxAmounts(amount, taxRate);
              updatedExpense.cgst = taxAmounts.cgst;
              updatedExpense.sgst = taxAmounts.sgst;
              updatedExpense.igst = taxAmounts.igst;
              updatedExpense.totalTax = taxAmounts.totalTax;
              updatedExpense.totalAmount = amount + taxAmounts.totalTax;
            } else {
              // No tax calculation for GST Not Applicable, Outside scope, GST with Zero Percent, or when no tax rate
              updatedExpense.cgst = 0;
              updatedExpense.sgst = 0;
              updatedExpense.igst = 0;
              updatedExpense.totalTax = 0;
              updatedExpense.totalAmount = amount;
            }
          }

          return updatedExpense;
        }
        return expense;
      });

      // Validate total amount after updating any field that affects totals
      if (field === 'amount' || field === 'tax_percentage') {
        const mainAmount = parseFloat(methods.getValues('amount')) || 0;
        const itemizedTotal = updatedExpenses.reduce((total, expense) => {
          const amount = typeof expense.amount === 'string' ? parseFloat(expense.amount) || 0 : expense.amount || 0;
          return total + amount;
        }, 0);

        // Clear validation errors
        setAmountValidationError('');
      }

      // Check minimum items validation
      const validItems = updatedExpenses.filter(expense =>
        expense.category && expense.itemName && expense.description && expense.amount > 0
      );
      if (validItems.length < 2) {
        setItemizedValidationError('Please add at least 2 valid items to use itemized mode');
      } else {
        setItemizedValidationError('');
      }

      return updatedExpenses;
    });
  };

  // Check if we're in edit mode or view mode
  const { expenseId, viewMode, createdBy } = location.state || {};
  const isEditMode = expenseId && expenseId !== null && !viewMode;
  const isViewMode = viewMode === true;

  const renderReceiptUpload = () => (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div
        className={`rounded text-center ${isDragOver ? 'bg-light' : ''}`}
        style={{
          width: '100%',
          height: '500px', // Increased height for better image display
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: isDragOver ? '#f8f9fa' : '#fafafa',
          transition: 'all 0.3s ease',
          position: 'relative',
          overflow: 'hidden',
          border: '2px dashed #d9d9d9',
          borderRadius: '8px',
          padding: 0 // Remove all padding
        }}
        onDragOver={isViewMode ? undefined : handleDragOver}
        onDragLeave={isViewMode ? undefined : handleDragLeave}
        onDrop={isViewMode ? undefined : handleDrop}
        onClick={isViewMode ? undefined : () => document.getElementById('file-upload')?.click()}
      >
        <input
          id="file-upload"
          type="file"
          multiple
          accept="image/*,.pdf,.doc,.docx,.xlsx,.xls,.csv"
          style={{ display: 'none' }}
          onChange={(e) => {
            handleFileUpload(e.target.files);
            e.target.value = '';
          }}
          disabled={isViewMode}
        />

        {/* Show uploaded image or PDF if any */}
        {uploadedFiles.length > 0 && (uploadedFiles.some(file => file.type.startsWith('image/')) || uploadedFiles.some(file => file.type === 'application/pdf')) ? (
          (() => {
            const imageFiles = getImageFiles();
            const pdfFiles = getPdfFiles();
            const hasImages = imageFiles.length > 0;
            const hasPdfs = pdfFiles.length > 0;
            const currentImage = hasImages ? getCurrentDisplayedImage() : null;
            const currentPdf = hasPdfs ? getCurrentDisplayedPdf() : null;
            const pdfIndex = currentPdf ? pdfFiles.findIndex(f => f === currentPdf) : -1;
            const currentPage = pdfIndex >= 0 ? (currentPdfPage[pdfIndex] || 1) : 1;
            const totalPages = pdfIndex >= 0 ? (pdfPageNumbers[pdfIndex] || 1) : 1;
            const pdfImageKey = pdfIndex >= 0 ? `${pdfIndex}-${currentPage}` : '';
            const pdfPageImage = pdfImageKey ? pdfPageImages[pdfImageKey] : null;
            const isLoadingPdf = pdfIndex >= 0 ? (loadingPdfPages[pdfIndex] || false) : false;

            // Show images first, then PDFs
            if (hasImages && currentImage) {
              return (
                <>
            <img
              src={
                (() => {
                  const imageFile = getCurrentDisplayedImage();
                  if (imageFile && (imageFile as any).originalUrl) {
                    // Use original URL for existing images
                    const originalUrl = (imageFile as any).originalUrl;
                    // Handle different URL formats
                    if (originalUrl.startsWith('data:')) {
                      return originalUrl; // Base64 data URL
                    } else if (originalUrl.startsWith('http')) {
                      return originalUrl; // HTTP URL
                    } else if (originalUrl.startsWith('/')) {
                      return originalUrl; // Relative path
                    } else {
                      // Assume it's a relative path or filename
                      return originalUrl;
                    }
                  } else if (imageFile) {
                    // Use object URL for newly uploaded files
                    return URL.createObjectURL(imageFile);
                  }
                  return '';
                })()
              }
              alt="Uploaded receipt"
              onClick={handleImageClick}
              style={{
                width: '100%',
                height: '100%',
                objectFit: isImageZoomed ? 'contain' : 'cover',
                borderRadius: '6px',
                backgroundColor: '#fff',
                position: 'absolute',
                top: 0,
                left: 0,
                padding: 0,
                margin: 0,
                transform: `scale(${imageZoom})`,
                transformOrigin: 'center center',
                transition: 'transform 0.3s ease',
                cursor: isImageZoomed ? 'zoom-out' : 'zoom-in'
              }}
            />


            {/* Zoom Controls */}
            <div style={{
              position: 'absolute',
              top: '20px',
              left: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              zIndex: 10
            }}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleZoomIn();
                }}
                style={{
                  background: 'rgba(0, 0, 0, 0.7)',
                  border: 'none',
                  borderRadius: '6px',
                  width: '36px',
                  height: '36px',
                  color: 'white',
                  fontSize: '18px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.9)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)';
                }}
                title="Zoom In"
              >
                +
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleZoomOut();
                }}
                style={{
                  background: 'rgba(0, 0, 0, 0.7)',
                  border: 'none',
                  borderRadius: '6px',
                  width: '36px',
                  height: '36px',
                  color: 'white',
                  fontSize: '18px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.9)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)';
                }}
                title="Zoom Out"
              >
                −
              </button>
              {isImageZoomed && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    resetZoom();
                  }}
                  style={{
                    background: 'rgba(0, 0, 0, 0.7)',
                    border: 'none',
                    borderRadius: '6px',
                    width: '36px',
                    height: '36px',
                    color: 'white',
                    fontSize: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.9)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)';
                  }}
                  title="Reset Zoom"
                >
                  1:1
                </button>
              )}
            </div>

            {/* Zoom Level Indicator */}
            {isImageZoomed && (
              <div style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                padding: '6px 12px',
                borderRadius: '16px',
                fontSize: '12px',
                fontWeight: '500',
                fontFamily: 'Inter, Roboto, sans-serif',
                zIndex: 10
              }}>
                {Math.round(imageZoom * 100)}%
              </div>
            )}
            
            {/* Image Navigation Arrows - Only show if multiple images */}
            {getImageFiles().length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    showPreviousImage();
                  }}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '12px',
                    transform: 'translateY(-50%)',
                    background: 'rgba(0, 0, 0, 0.6)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    color: 'white',
                    fontSize: '16px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    zIndex: 10
                  }}
                >
                  ‹
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    showNextImage();
                  }}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    right: '12px',
                    transform: 'translateY(-50%)',
                    background: 'rgba(0, 0, 0, 0.6)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    color: 'white',
                    fontSize: '16px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    zIndex: 10
                  }}
                >
                  ›
                </button>
              </>
            )}
            
            {/* Image Counter - Only show if multiple images */}
            {getImageFiles().length > 1 && (
              <div
                style={{
                  position: 'absolute',
                  bottom: '12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '16px',
                  fontSize: '12px',
                  fontFamily: 'Inter, Roboto, sans-serif',
                  zIndex: 10
                }}
              >
                {displayedImageIndex + 1} / {getImageFiles().length}
              </div>
            )}
            
            {!isViewMode && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const imageFile = getCurrentDisplayedImage();
                  if (imageFile) {
                    removeFile(uploadedFiles.findIndex(f => f === imageFile));
                  }
                }}
                style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  background: 'rgba(255, 77, 79, 0.9)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  color: 'white',
                  fontSize: '18px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  zIndex: 10
                }}
              >
                ×
              </button>
            )}
                </>
              );
            } else if (hasPdfs && currentPdf) {
              // Display PDF
              return (
                <>
                  {isLoadingPdf ? (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#fff'
                    }}>
                      <div className="spinner-border text-primary" role="status" style={{ width: '40px', height: '40px' }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div style={{
                        marginTop: '12px',
                        fontSize: '14px',
                        color: '#495057',
                        fontFamily: 'Inter, Roboto, sans-serif'
                      }}>
                        Loading PDF page...
                      </div>
                    </div>
                  ) : pdfPageImage ? (
                    <img
                      src={pdfPageImage}
                      alt={`PDF page ${currentPage}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        borderRadius: '6px',
                        backgroundColor: '#fff',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        padding: 0,
                        margin: 0
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#fff',
                      color: '#6c757d',
                      fontFamily: 'Inter, Roboto, sans-serif'
                    }}>
                      <Icon name="file-text" style={{ fontSize: '48px', marginBottom: '12px' }} />
                      <div>PDF loading...</div>
                    </div>
                  )}

                  {/* PDF Page Navigation Controls */}
                  {totalPages > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          showPreviousPdfPage(pdfIndex);
                        }}
                        disabled={currentPage <= 1}
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '12px',
                          transform: 'translateY(-50%)',
                          background: currentPage <= 1 ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.6)',
                          border: 'none',
                          borderRadius: '50%',
                          width: '32px',
                          height: '32px',
                          color: 'white',
                          fontSize: '16px',
                          cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                          zIndex: 10
                        }}
                        title="Previous Page"
                      >
                        ‹
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          showNextPdfPage(pdfIndex);
                        }}
                        disabled={currentPage >= totalPages}
                        style={{
                          position: 'absolute',
                          top: '50%',
                          right: '12px',
                          transform: 'translateY(-50%)',
                          background: currentPage >= totalPages ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.6)',
                          border: 'none',
                          borderRadius: '50%',
                          width: '32px',
                          height: '32px',
                          color: 'white',
                          fontSize: '16px',
                          cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                          zIndex: 10
                        }}
                        title="Next Page"
                      >
                        ›
                      </button>
                    </>
                  )}

                  {/* PDF Page Counter */}
                  {totalPages > 1 && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '12px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontFamily: 'Inter, Roboto, sans-serif',
                        zIndex: 10
                      }}
                    >
                      Page {currentPage} / {totalPages}
                    </div>
                  )}

                  {/* PDF Navigation Arrows - Only show if multiple PDFs */}
                  {pdfFiles.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          showPreviousPdf();
                        }}
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: pdfFiles.length > 1 && totalPages > 1 ? '50px' : '12px',
                          transform: 'translateY(-50%)',
                          background: 'rgba(0, 0, 0, 0.6)',
                          border: 'none',
                          borderRadius: '50%',
                          width: '32px',
                          height: '32px',
                          color: 'white',
                          fontSize: '16px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                          zIndex: 10
                        }}
                        title="Previous PDF"
                      >
                        «
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          showNextPdf();
                        }}
                        style={{
                          position: 'absolute',
                          top: '50%',
                          right: pdfFiles.length > 1 && totalPages > 1 ? '50px' : '12px',
                          transform: 'translateY(-50%)',
                          background: 'rgba(0, 0, 0, 0.6)',
                          border: 'none',
                          borderRadius: '50%',
                          width: '32px',
                          height: '32px',
                          color: 'white',
                          fontSize: '16px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                          zIndex: 10
                        }}
                        title="Next PDF"
                      >
                        »
                      </button>
                      <div
                        style={{
                          position: 'absolute',
                          top: '12px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          background: 'rgba(0, 0, 0, 0.7)',
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '16px',
                          fontSize: '12px',
                          fontFamily: 'Inter, Roboto, sans-serif',
                          zIndex: 10
                        }}
                      >
                        PDF {displayedPdfIndex + 1} / {pdfFiles.length}
                      </div>
                    </>
                  )}

                  {!isViewMode && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (currentPdf) {
                          removeFile(uploadedFiles.findIndex(f => f === currentPdf));
                        }
                      }}
                      style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        background: 'rgba(255, 77, 79, 0.9)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '36px',
                        height: '36px',
                        color: 'white',
                        fontSize: '18px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        zIndex: 10
                      }}
                    >
                      ×
                    </button>
                  )}
                </>
              );
            } else if (hasPdfs && currentPdf) {
              // Display PDF
              return (
                <>
                  {isLoadingPdf ? (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#fff'
                    }}>
                      <div className="spinner-border text-primary" role="status" style={{ width: '40px', height: '40px' }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div style={{
                        marginTop: '12px',
                        fontSize: '14px',
                        color: '#495057',
                        fontFamily: 'Inter, Roboto, sans-serif'
                      }}>
                        Loading PDF page...
                      </div>
                    </div>
                  ) : pdfPageImage ? (
                    <img
                      src={pdfPageImage}
                      alt={`PDF page ${currentPage}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        borderRadius: '6px',
                        backgroundColor: '#fff',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        padding: 0,
                        margin: 0
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#fff',
                      color: '#6c757d',
                      fontFamily: 'Inter, Roboto, sans-serif'
                    }}>
                      <Icon name="file-text" style={{ fontSize: '48px', marginBottom: '12px' }} />
                      <div>PDF loading...</div>
                    </div>
                  )}

                  {/* PDF Page Navigation Controls */}
                  {totalPages > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          showPreviousPdfPage(pdfIndex);
                        }}
                        disabled={currentPage <= 1}
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '12px',
                          transform: 'translateY(-50%)',
                          background: currentPage <= 1 ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.6)',
                          border: 'none',
                          borderRadius: '50%',
                          width: '32px',
                          height: '32px',
                          color: 'white',
                          fontSize: '16px',
                          cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                          zIndex: 10
                        }}
                        title="Previous Page"
                      >
                        ‹
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          showNextPdfPage(pdfIndex);
                        }}
                        disabled={currentPage >= totalPages}
                        style={{
                          position: 'absolute',
                          top: '50%',
                          right: '12px',
                          transform: 'translateY(-50%)',
                          background: currentPage >= totalPages ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.6)',
                          border: 'none',
                          borderRadius: '50%',
                          width: '32px',
                          height: '32px',
                          color: 'white',
                          fontSize: '16px',
                          cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                          zIndex: 10
                        }}
                        title="Next Page"
                      >
                        ›
                      </button>
                    </>
                  )}

                  {/* PDF Page Counter */}
                  {totalPages > 1 && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '12px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontFamily: 'Inter, Roboto, sans-serif',
                        zIndex: 10
                      }}
                    >
                      Page {currentPage} / {totalPages}
                    </div>
                  )}

                  {/* PDF Navigation Arrows - Only show if multiple PDFs */}
                  {pdfFiles.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          showPreviousPdf();
                        }}
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: pdfFiles.length > 1 && totalPages > 1 ? '50px' : '12px',
                          transform: 'translateY(-50%)',
                          background: 'rgba(0, 0, 0, 0.6)',
                          border: 'none',
                          borderRadius: '50%',
                          width: '32px',
                          height: '32px',
                          color: 'white',
                          fontSize: '16px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                          zIndex: 10
                        }}
                        title="Previous PDF"
                      >
                        «
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          showNextPdf();
                        }}
                        style={{
                          position: 'absolute',
                          top: '50%',
                          right: pdfFiles.length > 1 && totalPages > 1 ? '50px' : '12px',
                          transform: 'translateY(-50%)',
                          background: 'rgba(0, 0, 0, 0.6)',
                          border: 'none',
                          borderRadius: '50%',
                          width: '32px',
                          height: '32px',
                          color: 'white',
                          fontSize: '16px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                          zIndex: 10
                        }}
                        title="Next PDF"
                      >
                        »
                      </button>
                      <div
                        style={{
                          position: 'absolute',
                          top: '12px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          background: 'rgba(0, 0, 0, 0.7)',
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '16px',
                          fontSize: '12px',
                          fontFamily: 'Inter, Roboto, sans-serif',
                          zIndex: 10
                        }}
                      >
                        PDF {displayedPdfIndex + 1} / {pdfFiles.length}
                      </div>
                    </>
                  )}

                  {!isViewMode && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (currentPdf) {
                          removeFile(uploadedFiles.findIndex(f => f === currentPdf));
                        }
                      }}
                      style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        background: 'rgba(255, 77, 79, 0.9)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '36px',
                        height: '36px',
                        color: 'white',
                        fontSize: '18px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        zIndex: 10
                      }}
                    >
                      ×
                    </button>
                  )}
                </>
              );
            }
            return null;
          })()
        ) : (
          <>
            <Icon
              name="upload"
              className={`mb-3 ${isDragOver ? 'text-primary' : 'text-secondary'}`}
              style={{ fontSize: '64px' }} // Increased icon size
            />

            <h6 className={`mb-2 ${isDragOver ? 'text-primary' : 'text-muted'}`} style={{
              fontFamily: 'Inter, Roboto, sans-serif',
              fontSize: '18px',
              fontWeight: '500'
            }}>
              {isDragOver ? 'Drop files here' : 'Upload Receipts'}
            </h6>

            <p className="text-muted mb-3" style={{
              fontSize: '14px',
              fontFamily: 'Inter, Roboto, sans-serif',
              lineHeight: '1.5'
            }}>
              Drag and drop files here or{' '}
              <span className="text-primary" style={{
                cursor: 'pointer',
                fontFamily: 'Inter, Roboto, sans-serif',
                textDecoration: 'underline'
              }}>
                click to browse
              </span>
            </p>

            <div className="d-flex align-items-center justify-content-center gap-2 text-muted" style={{
              fontSize: '14px',
              fontFamily: 'Inter, Roboto, sans-serif',
              flexWrap: 'wrap',
              textAlign: 'center'
            }}>
              <Icon name="info" className="text-warning" />
              <span style={{ fontFamily: 'Inter, Roboto, sans-serif' }}>
                Supported formats: JPG, JPEG, PNG, PDF, DOCX, XLSX, XLS, CSV
              </span>
            </div>
          </>
        )}

        {/* OCR Processing Indicator */}
        {processingOCR && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'rgba(255, 255, 255, 0.95)',
            padding: '20px 30px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div className="spinner-border text-primary" role="status" style={{ width: '40px', height: '40px' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#495057',
              fontFamily: 'Inter, Roboto, sans-serif',
              textAlign: 'center'
            }}>
              Reading receipt with OCR...
            </div>
          </div>
        )}
      </div>

      {/* Add More Button - Outside drag and drop area */}
      {uploadedFiles.length > 0 && !isViewMode && (
        <div style={{ marginTop: '20px', textAlign: 'left' }}>
          <Button
            color="outline-primary"
            onClick={() => document.getElementById('file-upload')?.click()}
            style={{ 
              borderRadius: '8px',
              height: '36px',
              fontSize: '14px',
              fontWeight: '500',
              fontFamily: 'Inter, Roboto, sans-serif',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Icon name="plus" />
            Add More Files
          </Button>
        </div>
      )}

      {/* Uploaded Files List */}
      {uploadedFiles.length > 0 && (
        <div style={{
          marginTop: '12px',
          maxHeight: '180px',
          overflowY: 'auto',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          padding: '12px',
          border: '1px solid #e9ecef'
        }}>
          <h6 className="mb-3" style={{
            fontFamily: 'Inter, Roboto, sans-serif',
            fontSize: '14px',
            fontWeight: '600',
            color: '#495057',
            margin: '0 0 12px 0'
          }}>
            Uploaded Files ({uploadedFiles.length})
          </h6>
          <div className="list-group" style={{ gap: '8px' }}>
            {uploadedFiles.map((file, index) => (
              <div key={index} className="list-group-item d-flex justify-content-between align-items-center" style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #dee2e6',
                backgroundColor: '#fff',
                marginBottom: '4px'
              }}>
                <div className="d-flex align-items-center">
                  <Icon
                    name={file.type.startsWith('image/') ? 'image' : 'file-text'}
                    className="text-primary me-2"
                    style={{ fontSize: '16px' }}
                  />
                  <div>
                    <div className="fw-medium" style={{
                      fontFamily: 'Inter, Roboto, sans-serif',
                      fontSize: '13px',
                      color: '#495057'
                    }}>
                      {file.name}
                    </div>
                    <small className="text-muted" style={{
                      fontFamily: 'Inter, Roboto, sans-serif',
                      fontSize: '11px'
                    }}>
                      {(file as any).isExisting ? 'Existing file' : `${(file.size / 1024 / 1024).toFixed(2)} MB`}
                    </small>
                  </div>
                </div>
                {!isViewMode && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(index);
                    }}
                    style={{
                      padding: '4px 8px',
                      fontSize: '14px',
                      borderRadius: '4px'
                    }}
                  >
                    <Icon name="trash" style={{ fontSize: '14px' }} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <Head title={isViewMode ? "View Expense" : (isEditMode ? "Edit Expense" : "New Expense")} />
      <Content>
        <Card className="card-bordered">
          {/* Full card view for NewExpense without aside menu */}
          <div className="card-inner" style={{ minHeight: "87vh" }}>
            <FormProvider {...methods}>
              <div>
                <div
                  className="nk-ibx-head p-0 m-0 mt-1 w-100 d-flex justify-content-between align-items-center"
                  style={{ minHeight: "20px", border: "none" }}
                >
                  <div>
                    <h5 style={{ marginBottom: "5px" }}>{isViewMode ? "View Expense" : (isEditMode ? "Edit Expense" : "New Expense")}</h5>
                    {createdBy && (isEditMode || isViewMode) && (
                      <small className="text-muted" style={{ fontSize: "12px", fontWeight: "500" }}>
                        Created by: {createdBy}
                      </small>
                    )}
                  </div>
                  <div className="toggle-wrap nk-block-tools-toggle">
                    <ul className="nk-ibx-head-tools g-1 m-0 p-0">
                      <li className="pt-0 pb-0">
                        <Button
                          className="toggle"
                          color="primary"
                          size="sm"
                          onClick={handleBack}
                          style={{ borderRadius: "6px", height: "32px", fontSize: '12px', padding: '4px 12px', fontWeight: "500", fontFamily: 'Inter, Roboto, sans-serif' }}
                        >
                          <Icon name="arrow-left" />
                          <span>Back</span>
                        </Button>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Main Content - Full Screen Layout */}
                <div style={{ padding: '4px 0' }}>
                  <div style={{ minHeight: 'calc(100vh - 100px)' }}>

                    {/* Content Section */}
                    <div>
                      {(
                        <form onSubmit={(e) => {
                          e.preventDefault();
                          methods.handleSubmit(handleSubmit)(e);
                        }}>
                          <div style={{ display: 'flex', gap: '16px' }}>
                            {/* Left Section - Receipt Upload */}
                            <div style={{
                              width: '35%',
                              display: 'flex',
                              flexDirection: 'column'
                            }}>
                              {renderReceiptUpload()}
                            </div>

                            {/* Right Section - Expense Details (Scrollable) */}
                            <div style={{ width: '65%', display: 'flex', flexDirection: 'column' }}>
                              <div style={{
                                paddingRight: '8px'
                              }}>
                              <div className="row">
                                <div className="col-md-6 mb-3">
                                  <FormSelect
                                    name="report"
                                    label="Expense Header"
                                    required={true}
                                    placeholder="Select Expense Header"
                                    options={reportOptions}
                                    disabled={(location.state?.fromReportBreakDown && location.state?.reportId) || isViewMode}
                                  />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <FormDatePicker
                                      name="expenseDate"
                                      label="Expense Date"
                                      required={true}
                                      placeholder="eg: 31/01/2025"
                                      dateFormat="DD/MM/YYYY"
                                      minDate={selectedReportDateRange.startDate ? new Date(selectedReportDateRange.startDate) : undefined}
                                      maxDate={new Date()}
                                      disabled={isViewMode}
                                    />
                                  </div>
                                </div>
                                {/* Payment From and Vendor/Supplier fields */}
                                <div className="row">
                                  {/* Payment From field - only for admin users */}
                                  {currentUser?.genericRole?.toLowerCase() === "admin" ? (
                                    <div className="col-md-6 mb-3">
                                      <FormSelect
                                        name="userId"
                                        label="Payment From"
                                        placeholder="Select Employee"
                                        options={userOptions}
                                        required={true}
                                        disabled={loadingUsers || isViewMode}
                                      />
                                    </div>
                                  ) : null}
                                  {/* Vendor/Supplier field */}
                                  <div className={`${currentUser?.genericRole?.toLowerCase() === "admin" ? 'col-md-6' : 'col-md-6'} mb-3`}>
                                    <FormInput
                                      name="merchant"
                                      label="Vendor/Supplier"
                                      type="text"
                                      placeholder="Enter vendor/supplier name"
                                      required={true}
                                      disabled={isViewMode}
                                    />
                                  </div>
                                </div>
                                {/* Tax Type, Product Category, and Payment Mode row - when GST Not Applicable */}
                                {selectedTaxType === 'GST Not Applicable' && (
                                  <div className="row">
                                    <div className="col-md-4 mb-3">
                                      <FormSelect
                                        name="taxType"
                                        label="Tax Type"
                                        placeholder="Select Tax Type"
                                        required={true}
                                        options={[
                                          { value: 'GST Not Applicable', label: 'GST Not Applicable' },
                                          { value: 'Outside the scope of GST', label: 'Outside the scope of GST' },
                                          { value: 'GST Applicable', label: 'GST Applicable' },
                                          { value: 'GST with Zero Percent', label: 'GST with Zero Percent' }
                                        ]}
                                        onChange={(value) => setSelectedTaxType(value)}
                                        disabled={isViewMode}
                                      />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                      <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', display: 'block', fontFamily: 'Inter, Roboto, sans-serif' }}>
                                        Product Category <span style={{ color: 'red' }}>*</span>
                                      </label>
                                      <div className="form-check form-check-inline">
                                        <input
                                          type="radio"
                                          className="form-check-input"
                                          name="goodsOrService"
                                          value="Goods"
                                          checked={goodsOrService === 'Goods'}
                                          onChange={(e) => setGoodsOrService(e.target.value)}
                                          disabled={isViewMode}
                                        />
                                        <Label className="form-check-label">Goods</Label>
                                      </div>
                                      <div className="form-check form-check-inline">
                                        <input
                                          type="radio"
                                          className="form-check-input"
                                          name="goodsOrService"
                                          value="Service"
                                          checked={goodsOrService === 'Service'}
                                          onChange={(e) => setGoodsOrService(e.target.value)}
                                          disabled={isViewMode}
                                        />
                                        <Label className="form-check-label">Service</Label>
                                      </div>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                      <FormSelect
                                        name="paymentMode"
                                        label="Payment Mode"
                                        required={true}
                                        options={[
                                          { value: 'Cash', label: 'Cash' },
                                          { value: 'Bank Transfer', label: 'Bank Transfer' },
                                          { value: 'UPI', label: 'UPI' },
                                          { value: 'Cheque', label: 'Cheque' },
                                          { value: 'Credit', label: 'Credit' }
                                        ]}
                                        placeholder="Select Payment Mode"
                                        disabled={isViewMode}
                                      />
                                    </div>
                                  </div>
                                )}

                                {/* Tax Type and GSTIN row - when other tax types are selected */}
                                {selectedTaxType !== 'GST Not Applicable' && (
                                  <div className="row">
                                    <div className="col-md-6 mb-3">
                                      <FormSelect
                                        name="taxType"
                                        label="Tax Type"
                                        placeholder="Select Tax Type"
                                        required={true}
                                        defaultValue={selectedTaxType}
                                        options={[
                                          { value: 'GST Not Applicable', label: 'GST Not Applicable' },
                                          { value: 'Outside the scope of GST', label: 'Outside the scope of GST' },
                                          { value: 'GST Applicable', label: 'GST Applicable' },
                                          { value: 'GST with Zero Percent', label: 'GST with Zero Percent' }
                                        ]}
                                        onChange={(value) => setSelectedTaxType(value)}
                                        disabled={isViewMode}
                                      />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                      <FormInput
                                        name="gstin2"
                                        label="GSTIN"
                                        type="text"
                                        placeholder="Enter GSTIN"
                                        required={true}
                                        disabled={isViewMode}
                                        customValidation={{
                                          validate: (value: string) => {
                                            if (!value || value.trim() === '') {
                                              return true;
                                            }
                                            const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
                                            return gstinRegex.test(value.toUpperCase()) || 'Please enter a valid GSTIN format (e.g., 22AAAAA0000A1Z5)';
                                          }
                                        }}
                                      />
                                    </div>
                                  </div>
                                )}

                                {/* Category and Payment Mode row - when other tax types are selected */}
                                {selectedTaxType !== 'GST Not Applicable' && (
                                  <div className="row">
                                    <div className="col-md-6 mb-3">
                                      <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', display: 'block', fontFamily: 'Inter, Roboto, sans-serif' }}>
                                        Product Category <span style={{ color: 'red' }}>*</span>
                                      </label>
                                      <div className="form-check form-check-inline">
                                        <input
                                          type="radio"
                                          className="form-check-input"
                                          name="goodsOrService"
                                          value="Goods"
                                          checked={goodsOrService === 'Goods'}
                                          onChange={(e) => setGoodsOrService(e.target.value)}
                                          disabled={isViewMode}
                                        />
                                        <Label className="form-check-label">Goods</Label>
                                      </div>
                                      <div className="form-check form-check-inline">
                                        <input
                                          type="radio"
                                          className="form-check-input"
                                          name="goodsOrService"
                                          value="Service"
                                          checked={goodsOrService === 'Service'}
                                          onChange={(e) => setGoodsOrService(e.target.value)}
                                          disabled={isViewMode}
                                        />
                                        <Label className="form-check-label">Service</Label>
                                      </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                      <FormSelect
                                        name="paymentMode"
                                        label="Payment Mode"
                                        required={true}
                                        options={[
                                          { value: 'Cash', label: 'Cash' },
                                          { value: 'Bank Transfer', label: 'Bank Transfer' },
                                          { value: 'UPI', label: 'UPI' },
                                          { value: 'Cheque', label: 'Cheque' },
                                          { value: 'Credit', label: 'Credit' }
                                        ]}
                                        placeholder="Select Payment Mode"
                                        disabled={isViewMode}
                                      />
                                    </div>
                                  </div>
                                )}

                                {/* Cheque fields - shown only when Cheque is selected (consolidated for all tax types) */}
                                {paymentMode === 'Cheque' && (
                                  <div className="row">
                                    <div className="col-md-6 mb-3">
                                      <FormInput
                                        name="chequeNumber"
                                        label="Cheque Number"
                                        placeholder="Enter cheque number"
                                        type="text"
                                        required={true}
                                        disabled={isViewMode}
                                      />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                      <FormDatePicker
                                        name="chequeDate"
                                        label="Cheque Date"
                                        required={true}
                                        maxDate={new Date()}
                                        disabled={isViewMode}
                                      />
                                    </div>
                                  </div>
                                )}
                                {/* Tax Percentage and HSN/SAC Code fields - Only show when GST Applicable is selected and not itemized */}
                                {selectedTaxType === 'GST Applicable' && !isItemized && (
                                  <div className="row">
                                    <div className="col-md-6 mb-3">
                                      <FormSelect
                                        name="tax_percentage" //tax_percentage
                                        label="Tax Percentage (%)"
                                        placeholder="Select tax percentage"
                                        required={true}
                                        options={[
                                          { value: '0', label: '0%' },
                                          { value: '5', label: '5%' },
                                          { value: '12', label: '12%' },
                                          { value: '18', label: '18%' },
                                          { value: '28', label: '28%' }
                                        ]}
                                        onChange={(value) => handleHeaderTaxPercentChange(value)}
                                        disabled={isViewMode}
                                      />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                      <FormInput
                                        name="hsnSac"
                                        label={goodsOrService === 'Goods' ? 'HSN Code' : 'SAC Code'}
                                        type="text"
                                        placeholder={goodsOrService === 'Goods' ? 'Enter HSN Code' : 'Enter SAC Code'}
                                        required={false}
                                        disabled={isViewMode}
                                      />
                                    </div>
                                  </div>
                                )}

                                {/* Destination of Supply field - Show when Goods is selected (edit and create) */}
                                {goodsOrService === 'Goods' && (
                                  <div className="row">
                                    <div className="col-md-6 mb-3">
                                      <FormInput
                                        name="destinationOfSupply3"
                                        label="Destination Of Supply"
                                        type="text"
                                        placeholder="Enter destination of supply"
                                        required={true}
                                        disabled={isViewMode}
                                      />
                                    </div>
                                  </div>
                                )}
                                {/* Amount section - Hide when itemized */}
                                {!isItemized && (
                                  <div className="mb-3">
                                    <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', display: 'block', fontFamily: 'Inter, Roboto, sans-serif' }}>
                                      Total Amount <span style={{ color: 'red' }}>*</span>
                                    </label>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                      <div style={{ width: '80px' }}>
                                        <FormSelect
                                          name="currency"
                                          label=""
                                          defaultValue="INR"
                                          options={[
                                            { value: 'INR', label: 'INR' },
                                            { value: 'USD', label: 'USD' },
                                            { value: 'EUR', label: 'EUR' }
                                          ]}
                                          customStyle={{ height: '36px' }}
                                          disabled={isViewMode}
                                        />
                                      </div>
                                      <div style={{ flex: 1 }}>
                                        <FormInput
                                          name="amount"
                                          label=""
                                          type="number"
                                          placeholder="Enter Total amount"
                                          onInput={(e) => handleMainAmountChange(e.currentTarget.value)}
                                          disabled={isViewMode}
                                        />
                                      </div>
                                      <Button
                                        color="primary"
                                        size="sm"
                                        onClick={handleItemizeToggle}
                                        disabled={isViewMode}
                                        style={{ borderRadius: "6px", height: "32px", fontSize: '14px', padding: '4px 12px' }}
                                      >
                                        Item Split
                                      </Button>
                                    </div>

                                    {/* Tax Amount Display - Only show when GST Applicable and not itemized */}
                                    {selectedTaxType === 'GST Applicable' && (
                                      <div style={{
                                        marginTop: '12px',
                                        padding: '12px',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '6px',
                                        border: '1px solid #e9ecef'
                                      }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                          <span style={{ fontSize: '14px', fontWeight: '500', color: '#495057', fontFamily: 'Inter, Roboto, sans-serif' }}>
                                            Subtotal (₹):
                                          </span>
                                          <span style={{ fontSize: '14px', fontWeight: '500', color: '#495057', fontFamily: 'Inter, Roboto, sans-serif' }}>
                                            ₹{(watchedAmount ? parseFloat(watchedAmount) : 0).toFixed(2)}
                                          </span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                          <span style={{ fontSize: '14px', fontWeight: '500', color: '#495057', fontFamily: 'Inter, Roboto, sans-serif' }}>
                                            Tax Amount (₹):
                                          </span>
                                          <span style={{ fontSize: '14px', fontWeight: '500', color: '#495057', fontFamily: 'Inter, Roboto, sans-serif' }}>
                                            ₹{headerTaxAmount.toFixed(2)}
                                          </span>
                                        </div>
                                        <div style={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          paddingTop: '8px',
                                          borderTop: '1px solid #dee2e6'
                                        }}>
                                          <span style={{ fontSize: '16px', fontWeight: '700', color: '#495057', fontFamily: 'Inter, Roboto, sans-serif' }}>
                                            Total Amount (₹):
                                          </span>
                                          <span style={{ fontSize: '16px', fontWeight: '700', color: '#495057', fontFamily: 'Inter, Roboto, sans-serif' }}>
                                            ₹{headerTotalAmount.toFixed(2)}
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}


                                {/* Show toggle button when itemized */}
                                {isItemized && (
                                  <div className="mb-3">
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={handleItemizeToggle}
                                      disabled={isViewMode}
                                      style={{ borderRadius: "6px", height: "32px", fontSize: '14px', padding: '4px 12px' }}
                                    >
                                      Hide Split
                                    </Button>
                                    <div className="text-muted mt-1" style={{ fontSize: '12px', fontFamily: 'Inter, Roboto, sans-serif' }}>
                                      Please add at least 2 items to use item split mode
                                    </div>
                                  </div>
                                )}

                                {/* Itemized Expenses Table - Only show when itemized */}
                                {isItemized && (() => {
                                  // Filter expenses based on the selected goodsOrService type
                                  const filteredExpenses = itemizedExpenses.filter(expense => expense.type === goodsOrService);

                                  // Define columns for Ant Design Table
                                  const columns = [
                                    {
                                      title: (
                                        <span>
                                          ITEM CATEGORY <span style={{ color: 'red' }}>*</span>
                                        </span>
                                      ),
                                      dataIndex: 'category',
                                      key: 'category',
                                      width: '22%',
                                      render: (_: any, record: any) => (
                                        <Select
                                          placeholder="Select category"
                                          value={record.category || undefined}
                                          onChange={(value) => handleItemizedExpenseChange(record.id, 'category', value)}
                                          disabled={isViewMode}
                                          style={{
                                            width: '100%',
                                            height: '32px',
                                            fontSize: '14px',
                                            fontFamily: 'Inter, Roboto, sans-serif'
                                          }}
                                          dropdownStyle={{
                                            fontSize: '14px',
                                            fontFamily: 'Inter, Roboto, sans-serif'
                                          }}
                                          allowClear
                                          options={[
                                            { value: 'Travel', label: 'Travel' },
                                            { value: 'Utilities', label: 'Utilities' },
                                            { value: 'Electronics', label: 'Electronics' },
                                            { value: 'Accommodation', label: 'Accommodation' },
                                            { value: 'Entertainment', label: 'Entertainment' },
                                            { value: 'Miscellaneous', label: 'Miscellaneous' },
                                            { value: 'Tech_invoices', label: 'Tech Invoices' },
                                            { value: 'Pantry_expenses', label: 'Pantry Expenses' }
                                          ]}
                                        />
                                      ),
                                    },
                                    {
                                      title: (
                                        <span>
                                          ITEM NAME <span style={{ color: 'red' }}>*</span>
                                        </span>
                                      ),
                                      dataIndex: 'description',
                                      key: 'description',
                                      width: '18%',
                                      render: (_: any, record: any) => (
                                        <Input
                                          placeholder="Enter item name"
                                          value={record.itemName || ''}
                                          onChange={(e) => handleItemizedExpenseChange(record.id, 'itemName', e.target.value)}
                                          disabled={isViewMode}
                                          style={{
                                            fontSize: '14px',
                                            fontFamily: 'Inter, Roboto, sans-serif',
                                            height: '32px',
                                            border: '1px solid #d9d9d9',
                                            borderRadius: '6px'
                                          }}
                                        />
                                      ),
                                    },
                                    ...(selectedTaxType === 'GST Applicable' ? [
                                      {
                                        title: (
                                          <span>
                                            TAX <span style={{ color: 'red' }}>*</span>
                                          </span>
                                        ),
                                        dataIndex: 'tax_percentage',
                                        key: 'tax_percentage',
                                        width: '18%',
                                        render: (_: any, record: any) => (
                                          <Select
                                            placeholder="Select"
                                            value={record.tax_percentage || undefined}
                                            onChange={(value) => handleItemizedExpenseChange(record.id, 'tax_percentage', value)}
                                            disabled={isViewMode}
                                            style={{
                                              width: '100%',
                                              height: '32px',
                                              fontSize: '14px',
                                              fontFamily: 'Inter, Roboto, sans-serif'
                                            }}
                                            dropdownStyle={{
                                              fontSize: '14px',
                                              fontFamily: 'Inter, Roboto, sans-serif'
                                            }}
                                            allowClear
                                            options={[
                                              { value: '0%', label: '0%' },
                                              { value: '5%', label: '5%' },
                                              { value: '12%', label: '12%' },
                                              { value: '18%', label: '18%' },
                                              { value: '28%', label: '28%' },
                                            ]}
                                          />
                                        ),
                                      },
                                      {
                                        title: (
                                          <span>
                                            {goodsOrService === 'Goods' ? 'HSN CODE' : 'SAC CODE'} <span style={{ color: 'red' }}>*</span>
                                          </span>
                                        ),
                                        dataIndex: 'hsnSac',
                                        key: 'hsnSac',
                                        width: '14%',
                                        render: (_: any, record: any) => (
                                          <Input
                                            placeholder={goodsOrService === 'Goods' ? 'Enter HSN Code' : 'Enter SAC Code'}
                                            value={record.hsnSac || ''}
                                            onChange={(e) => handleItemizedExpenseChange(record.id, 'hsnSac', e.target.value)}
                                            disabled={isViewMode}
                                            style={{
                                              fontSize: '14px',
                                              fontFamily: 'Inter, Roboto, sans-serif',
                                              height: '32px',
                                              border: '1px solid #d9d9d9',
                                              borderRadius: '6px'
                                            }}
                                          />
                                        ),
                                      }
                                    ] : []),
                                    ...(selectedTaxType !== 'GST Applicable' ? [
                                      {
                                        title: (
                                          <span>
                                            {goodsOrService === 'Goods' ? 'HSN CODE' : 'SAC CODE'}
                                          </span>
                                        ),
                                        dataIndex: 'hsnSac',
                                        key: 'hsnSac',
                                        width: '14%',
                                        render: (_: any, record: any) => (
                                          <Input
                                            placeholder={goodsOrService === 'Goods' ? 'Enter HSN Code' : 'Enter SAC Code'}
                                            value={record.hsnSac || ''}
                                            onChange={(e) => handleItemizedExpenseChange(record.id, 'hsnSac', e.target.value)}
                                            disabled={isViewMode}
                                            style={{
                                              fontSize: '14px',
                                              fontFamily: 'Inter, Roboto, sans-serif',
                                              height: '32px',
                                              border: '1px solid #d9d9d9',
                                              borderRadius: '6px'
                                            }}
                                          />
                                        ),
                                      }
                                    ] : []),
                                    {
                                      title: (
                                        <span>
                                          AMOUNT <span style={{ color: 'red' }}>*</span>
                                        </span>
                                      ),
                                      dataIndex: 'amount',
                                      key: 'amount',
                                      width: '12%',
                                      render: (_: any, record: any) => (
                                        <Input
                                          type="number"
                                          placeholder="0.00"
                                          value={record.amount || ''}
                                          onChange={(e) => handleItemizedExpenseChange(record.id, 'amount', e.target.value)}
                                          disabled={isViewMode}
                                          style={{
                                            fontSize: '14px',
                                            fontFamily: 'Inter, Roboto, sans-serif',
                                            height: '32px',
                                            border: '1px solid #d9d9d9',
                                            borderRadius: '6px'
                                          }}
                                        />
                                      ),
                                    },
                                    {
                                      title: (
                                        <span>
                                          DESCRIPTION <span style={{ color: 'red' }}>*</span>
                                        </span>
                                      ),
                                      dataIndex: 'description',
                                      key: 'description',
                                      width: selectedTaxType === 'GST Applicable' ? '30%' : '35%',
                                      render: (_: any, record: any) => (
                                        <Input
                                          placeholder="Enter description"
                                          value={record.description || ''}
                                          onChange={(e) => handleItemizedExpenseChange(record.id, 'description', e.target.value)}
                                          disabled={isViewMode}
                                          style={{
                                            fontSize: '14px',
                                            fontFamily: 'Inter, Roboto, sans-serif',
                                            height: '32px',
                                            border: '1px solid #d9d9d9',
                                            borderRadius: '6px'
                                          }}
                                        />
                                      ),
                                    },
                                    {
                                      title: '',
                                      key: 'actions',
                                      width: '5%',
                                      align: 'center' as const,
                                      render: (_: any, record: any) => (
                                        <Button
                                          color="link"
                                          bsSize="sm"
                                          onClick={() => removeItemizedExpenseRow(record.id)}
                                          disabled={isViewMode}
                                          style={{
                                            color: '#ff4d4f',
                                            padding: '4px',
                                            height: '32px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                          }}
                                        >
                                          <Icon name="trash" />
                                        </Button>
                                      ),
                                    },
                                  ];

                                  return (
                                    <div style={{ marginTop: '20px' }}>
                                      <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '16px'
                                      }}>
                                        <div>
                                          <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '500', fontFamily: 'Inter, Roboto, sans-serif' }}>
                                            Itemized Expenses - {goodsOrService}
                                          </h5>
                                          {/* Display validation error at header level */}
                                          {amountValidationError && (
                                            <div style={{
                                              color: '#ff4d4f',
                                              fontSize: '12px',
                                              marginTop: '4px',
                                              fontFamily: 'Inter, Roboto, sans-serif',
                                              fontWeight: '500'
                                            }}>
                                              {amountValidationError}
                                            </div>
                                          )}
                                        </div>
                                        <Button
                                          color="link"
                                          onClick={addItemizedExpenseRow}
                                          disabled={isViewMode}
                                          style={{ color: '#1890ff', padding: 0 }}
                                        >
                                          <Icon name="plus" className="me-1" />
                                          Add Another Line
                                        </Button>
                                      </div>

                                      <div style={{
                                        border: '1px solid #f0f0f0',
                                        borderRadius: '8px',
                                        overflow: 'auto'
                                      }}>
                                        <Table
                                          columns={columns}
                                          dataSource={filteredExpenses}
                                          rowKey="id"
                                          pagination={false}
                                          size="middle"
                                          bordered={false}
                                          scroll={{ y: 300, x: 'max-content' }}
                                          components={{
                                            header: {
                                              cell: (props: any) => (
                                                <th
                                                  {...props}
                                                  style={{
                                                    ...props.style,
                                                    position: 'sticky',
                                                    top: 0,
                                                    backgroundColor: '#fafafa',
                                                    zIndex: 1,
                                                    borderBottom: '1px solid #f0f0f0',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    fontFamily: 'Inter, Roboto, sans-serif',
                                                    padding: '12px',
                                                    textAlign: 'left',
                                                    whiteSpace: 'nowrap'
                                                  }}
                                                />
                                              ),
                                            },
                                          }}
                                          style={{
                                            height: '100%',
                                          }}
                                        />
                                      </div>

                                      {filteredExpenses.length === 0 && (
                                        <div style={{
                                          border: '1px dashed #d9d9d9',
                                          borderRadius: '8px',
                                          padding: '20px',
                                          textAlign: 'center',
                                          color: '#999',
                                          marginTop: '16px'
                                        }}>
                                          No {goodsOrService.toLowerCase()} items added yet
                                        </div>
                                      )}

                                      <div style={{
                                        marginTop: '16px',
                                        padding: '16px',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '8px',
                                        border: '1px solid #e9ecef'
                                      }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                          <span style={{ fontSize: '14px', fontWeight: '500', color: '#495057', fontFamily: 'Inter, Roboto, sans-serif' }}>
                                            Subtotal (₹):
                                          </span>
                                          <span style={{ fontSize: '14px', fontWeight: '500', color: '#495057', fontFamily: 'Inter, Roboto, sans-serif' }}>
                                            ₹{(expenseSubtotalFromApi ?? filteredExpenses.reduce((total, expense) => total + (typeof expense.amount === 'string' ? parseFloat(expense.amount) || 0 : expense.amount || 0), 0)).toFixed(2)}
                                          </span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                          <span style={{ fontSize: '14px', fontWeight: '500', color: '#495057', fontFamily: 'Inter, Roboto, sans-serif' }}>
                                            Total Tax (₹):
                                          </span>
                                          <span style={{ fontSize: '14px', fontWeight: '500', color: '#495057', fontFamily: 'Inter, Roboto, sans-serif' }}>
                                            ₹{(expenseTotalTaxFromApi ?? filteredExpenses.reduce((total, expense) => total + (typeof expense.totalTax === 'string' ? parseFloat(expense.totalTax) || 0 : expense.totalTax || 0), 0)).toFixed(2)}
                                          </span>
                                        </div>
                                        <div style={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          paddingTop: '8px',
                                          borderTop: '1px solid #dee2e6'
                                        }}>
                                          <span style={{ fontSize: '18px', fontWeight: '700', color: '#495057', fontFamily: 'Inter, Roboto, sans-serif' }}>
                                            Total Amount (₹):
                                          </span>
                                          <span style={{ fontSize: '18px', fontWeight: '700', color: '#495057', fontFamily: 'Inter, Roboto, sans-serif' }}>
                                            ₹{(expenseTotalAmountFromApi ?? filteredExpenses.reduce((total, expense) => total + (typeof expense.totalAmount === 'string' ? parseFloat(expense.totalAmount) || 0 : expense.totalAmount || 0), 0)).toFixed(2)}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()}
                                <div className="mb-3">
                                  <FormInput
                                    name="reference"
                                    label="Reference#"
                                    type="text"
                                    placeholder="Enter Reference Number"
                                    required={true}
                                    disabled={isViewMode}
                                  />
                                </div>

                                <div className="mb-3">
                                  <div className="form-check">
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      name="reimbursable"
                                      checked={methods.watch('reimbursable')}
                                      onChange={(e) => methods.setValue('reimbursable', e.target.checked)}
                                      disabled={isViewMode}
                                    />
                                    <Label className="form-check-label" style={{ fontSize: '14px', fontFamily: 'Inter, Roboto, sans-serif' }}>
                                      Claim reimbursement
                                    </Label>
                                  </div>
                                </div>

                                <div className="mb-3">
                                  <FormTextArea
                                    name="description"
                                    label="Description"
                                    required={true}
                                    placeholder="Enter Description"
                                    maxLength={500}
                                    rows={3}
                                    disabled={isViewMode}
                                  />
                                </div>

                                {/* Action Buttons - Hide in view mode */}
                                {!isViewMode && (
                                  <div style={{ marginTop: '20px', paddingTop: '20px' }}>
                                    <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                                      <li>
                                        <Button
                                          className="btn btn-primary btn-md"
                                          color="primary"
                                          type="submit"
                                          disabled={loading}
                                          style={{ borderRadius: '6px', height: '32px', fontSize: '12px', padding: '4px 12px', fontWeight: '500', fontFamily: 'Inter, Roboto, sans-serif' }}
                                        >
                                          {isEditMode ? "Update" : "Save"}
                                        </Button>
                                      </li>
                                      <li>
                                        <a
                                          href="#cancel"
                                          onClick={(ev) => {
                                            ev.preventDefault();
                                            handleCancel();
                                          }}
                                          className="link link-light"
                                        >
                                          Cancel
                                        </a>
                                      </li>
                                    </ul>
                                  </div>
                                )}
                                
                              </div>
                            </div>
                          </div>
                        </form>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </FormProvider>
          </div>
        </Card>
      </Content>
      <ReportInDetailModal />
    </>
  );
};

export default NewExpense;